"use client"

import { useState, useContext, useEffect, useRef, useMemo } from "react"
import { useParams, useLocation } from "react-router-dom"
import { useTenantNavigation } from "../../hooks/useTenantNavigation"
import { ErpContext } from "../../context/ErpContext.js"
import Cookies from "js-cookie"
import { formatInputWithSeparators, parseLocalizedNumber, getLocaleConfig } from "../../utils/numeros.js"

// Material UI Components
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Checkbox,
  Collapse,
  Modal,
  Divider,
  Tooltip,
  FormControlLabel,
  InputAdornment,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  alpha,
  FormControl,
  Select,
  MenuItem,
  Drawer,
} from "@mui/material"

// Material UI Icons
import {
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  Visibility as VisibilityIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  PersonAddAlt as PersonAddAltIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  PointOfSale as PointOfSaleIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material"

// Components
import ModalEntidadCliente from "../adminComponent/ModalEntidadClienteComponent.js"
import CrearEntidadClienteModalComponent from "../adminComponent/CrearEntidadClienteModalComponent.js"
import ModalFacturaComponent from "./ModalFacturaComponent.js"
import "./GestionGenerarVentaComponent.css"
import CashPaymentModal from "./cash-payment-modal.js"
import DescripcionChequeModal from "./DescripcionChequeModal.js"
import SeleccionLotesModal from "./SeleccionLotesModal.js"
import MobileVentaLayout from "./estructurasMobiles/MobileVentaLayout.js"
import VentaConfigPanel from "./estructurasMobiles/VentaConfigPanel.js"

// Styled Table Components
import { styled } from "@mui/material/styles"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell, { tableCellClasses } from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableFooter from "@mui/material/TableFooter"

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#5b5b5f",
    color: theme.palette.common.white,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    [theme.breakpoints.down("sm")]: {
      fontSize: 13,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.1),
    transition: "background-color 0.2s ease",
  },
  // Reducir la altura de las filas
  "& .MuiTableCell-root": {
    padding: "6px 8px", // Reducir padding vertical y horizontal
    height: "40px", // Altura fija más compacta
  },
}))

// Función helper para redondear a 2 decimales (operaciones monetarias)
const roundToTwoDecimals = (value) => {
  return Math.round(value * 100) / 100
}

// Mantener alias para compatibilidad con código existente
const truncateToTwoDecimals = roundToTwoDecimals


/**
 * Componente principal para generar ventas
 *
 * FUNCIONALIDADES IMPLEMENTADAS:
 * 1. Búsqueda y selección de productos
 * 2. Gestión de clientes y medios de pago
 * 3. Cálculo de precios, descuentos e impuestos
 * 4. Generación de facturas AFIP
 *
 * NUEVA FUNCIONALIDAD - PRODUCTOS DE BALANZA:
 * - Detección automática de códigos de balanza (prefijo "20")
 * - Formato: 2-5-5 + 1 dígito de control
 * - Ejemplo: 20012340050001 = PLU(01234) + Peso(0.5kg)
 * - La cantidad se establece automáticamente como el peso en kg
 * - Se mantiene toda la lógica existente de precios y validaciones
 *
 * USO DE PRODUCTOS DE BALANZA:
 * 1. Escribir o escanear código de balanza en "Buscar producto"
 * 2. El sistema detecta automáticamente el código y establece la cantidad
 * 3. Se busca el producto por PLU de balanza
 * 4. Hacer clic en "Agregar" (botón con icono de carrito) para agregarlo
 */
const GestionGenerarVentaComponent = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"))
  const isMobileOrTablet = isMobile || isTablet
  const location = useLocation()
  const zonaCliente = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Argentina/Buenos_Aires"
  const { id } = useParams()
  const navigate = useTenantNavigation()

  // Context
  const {
    getItemTresLetras,
    itemTresLetras,
    arrayItemTresLetras,
    entidadesClientesTresLetras,
    getLoteMasAntiguoConStock,
    descontarStockVenta,
    getSugerenciaLotesVenta,
    getLotesDisponiblesVenta,
    getLotesDeTransaccion,
    getEntidadesClientesTresLetras,
    postTransaccionItem,
    postTransaccion,
    putTransaccion,
    getTipoEntidad,
    tipoEntidades,
    tipoMedioDePago,
    getTipoMedioDePago,
    updateItemUbicacion,
    postCrearPago,
    postTransaccionPago,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
    getCuentaCorriente,
    getCuentaCorrienteByIdEntidad,
    postCuentaCorriente,
    updateCuentaCorriente,
    cuentasCorrientes,
    getUsuario,
    usuarios,
    transaccionItem,
    postTransaccionImpuesto,
    getEntidadByID,
    getTransaccionFilter,
    getTransaccionItemsByTransactionId,
    deleteTransaccionItem,
    deleteTransaccionPago,
    getItemUbicacionFilterByUbicacion,
    getItemById,
    impuestos,
    arrayImpuestos,
    getImpuestos,
    itemAtributoNoEliminados,
    getItemAtributoNoEliminados,
    getUbicacion,
    ubicaciones,
    getNegocio,
    negocios,
    tiposFacturas,
    getTiposFacturas,
    postTransaccionTipoFactura,
    ivaHardcoded,
    getTop10ItemVentas,
    getListaMontosByIdItemIdEntidad,
    getIncluirImpuestos,
    enviarFacturaAfip,
    isNavbarCollapsed,
    getFechaHoraActualClient,
    getParametrosGlobales,
    updateItemPrecioListaId,
    getImpuestoItem,
    getIvaAFIP,
    postTransaccionImpuestoItem,
    updateTransaccionImpuestoItem,
    updateImpuestoItem,
    getCondicionIvaByIdEntidad,
    user, // 🔒 Usuario del contexto para verificar permisos
    tieneAccesoFuncionalidad, // 🔒 Función de verificación de permisos granulares
    formatoNumericoGlobal,
    deleteTransaccionItemNoLista,
    updateTransaccionItem,
    revertirLotesDeTransaccion,
  } = useContext(ErpContext)

  // Estados principales
  const [descripcionTransaccion, setDescripcionTransaccion] = useState("")
  const [selectedItem, setSelectedItem] = useState(null)
  const [searchText, setSearchText] = useState("")
  const [searchTextCliente, setSearchTextCliente] = useState("")
  const [filteredItems, setFilteredItems] = useState([])
  const [itemList, setItemList] = useState([])
  const [cantidadTransaccionItem, setCantidadTransaccionItem] = useState("")
  // dniCliente ahora representa la entidad cliente seleccionada; se inicializa en null
  const [dniCliente, setDniCliente] = useState(null)
  
  // 🆕 Estados para gestión de lotes en ventas
  const [lotesDelCarrito, setLotesDelCarrito] = useState({}) // { idItem: [{ idLote, numeroLote, cantidad, fechaVenc }] }
  const [modalLotesAbierto, setModalLotesAbierto] = useState(false)
  const [itemLoteSeleccionado, setItemLoteSeleccionado] = useState(null)
  const [gestionDeLotesActiva, setGestionDeLotesActiva] = useState(false)
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState([])
  
  // 🔄 Ref para rastrear el tipo de medio de pago anterior (evitar limpiar en carga inicial)
  const prevTipoMedioDePagoRef = useRef(null)

  // Estados para precios y cantidades
  const [precioTotal, setPrecioTotal] = useState(0)
  const [precioBaseFinalConDescuento, setPrecioBaseFinalConDescuento] = useState(0)
  const [precioTotalConImpuestos, setPrecioTotalConImpuestos] = useState(0)
  const [cantidadItemsSelect, setCantidadItemsSelect] = useState(0)
  const [cantidadItemsInvocados, setCantidadItemsInvocados] = useState(0)

  // Estados para entidades y clientes
  const [entidadExistente, setEntidadExistente] = useState(null)
  const [abrirModalCrearCliente, setAbrirModalCrearCliente] = useState(false)
  const [abrirModalEntidadCliente, setAbrirModalEntidadCliente] = useState(false)
  const [entidadAEditar, setEntidadAEditar] = useState(null)
  const [isEditing, setIsEditing] = useState(false)

  // Estados para descuentos
  const [valorDescuento, setValorDescuento] = useState(0)
  const [porcentajeDescuento, setPorcentajeDescuento] = useState(0)
  const [tempValorDescuento, setTempValorDescuento] = useState("0")
  const [tempPorcentajeDescuento, setTempPorcentajeDescuento] = useState("0")
  const [tempPorcentajeInteres, setTempPorcentajeInteres] = useState("0")
  const [precioBaseTotal, setPrecioBaseTotal] = useState(0)
  const [montoTotalDescuentos, setMontoTotalDescuentos] = useState(0)
  const [montoTotalIntereses, setMontoTotalIntereses] = useState(0)
  const [aplicarDescuento, setAplicarDescuento] = useState(false)
  const [aplicarInteres, setAplicarInteres] = useState(false)

  // Estados para modales
  const [openModal, setOpenModal] = useState(false)
  const [openModalMedioPago, setOpenModalMedioPago] = useState(false)
  const [checkboxFacturaNegro, setCheckboxFacturaNegro] = useState(false)
  const [aceptarImpuestos, setAceptarImpuestos] = useState(false)
  const [openModalCashPayment, setOpenModalCashPayment] = useState(false)
  
  // Estado para panel de configuración móvil
  const [mobileConfigPanelOpen, setMobileConfigPanelOpen] = useState(false)

  // Estados para medios de pago
  const [modalMedioDePagoSeleccionadoID, setModalMedioDePagoSeleccionadoID] = useState(null)
  const [modalTipoMedioDePagoSeleccionado, setModalTipoMedioDePagoSeleccionado] = useState(null)
  const [modalMedioDePagoSeleccionado, setModalMedioDePagoSeleccionado] = useState(null)
  const [modalMedioDePagoCantidad, setModalMedioDePagoCantidad] = useState(null)
  const [medioDePagoList, setMedioDePagoList] = useState([])
  const [cantidaMedioDePagoList, setCantidaMedioDePagoList] = useState(0)
  const [selectedTipoMedioDePago, setSelectedTipoMedioDePago] = useState(null)
  const [selectedMedioDePago, setSelectedMedioDePago] = useState(null)
  const [openCollapse, setOpenCollapse] = useState(false)
  const [mostrarButtonModalMedioDePago, setMostrarButtonModalMedioDePago] = useState(false)
  const [medioDePagoEspecificoSeleccionadoModal, setMedioDePagoEspecificoSeleccionadoModal] = useState(null)
  const [itemImpuestos, setItemImpuestos] = useState({})

  const [cantidadInputs, setCantidadInputs] = useState({})
  const [precioInputs, setPrecioInputs] = useState({}) // Valores formateados para precios

  // Obtener configuración de locale (memoizado para evitar recálculos)
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  // Estados para transacciones y facturas
  const [transaccionData, setTransaccionData] = useState(null)
  const [itemsTransaccion, setItemsTransaccion] = useState([])
  const [entidadFactura, setEntidadFactura] = useState([])
  const [filteredTransaccionItems, setFilteredTransaccionItems] = useState([])
  const [attributeValues, setAttributeValues] = useState([])
  const [mostrarIva, setMostrarIva] = useState(false)
  const [idTipoFactura, setIdTipoFactura] = useState(null)
  const [impuestosModificados, setImpuestosModificados] = useState({})

  // Estados para ubicaciones y negocios
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const [selectedNegocio, setSelectedNegocio] = useState(null)
  const [topVentasItems, set10VentasItems] = useState(null)

  const [scannerActivado, setScannerActivado] = useState(false)

  // 🔒 VERIFICAR PERMISO GRANULAR: Precio editable en ventas
  // IDs definidos en: server/services/menuAccesoValidator.js (VENTAS1)
  const precioEnVentaEditable = tieneAccesoFuncionalidad(user, 'VENTAS1', 'precioEnVentaEditable')

  // Estados para UI
  const [loading, setLoading] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  })

  // Referencias
  const isFirstRender = useRef(true)
  const autocompleteRef = useRef(null)
  const cantidadRef = useRef(null)
  const buttonRef = useRef(null)

  const [cashPaidAmount, setCashPaidAmount] = useState(null)
  const [hasOpenedCashModal, setHasOpenedCashModal] = useState(false)
  const [hasOpenedCuitModal, setHasOpenedCuitModal] = useState(false)

  // Estados para gestión de lotes

  const [descripcionCheque, setDescripcionCheque] = useState(null)
  const [hasOpenedChequeModal, setHasOpenedChequeModal] = useState(false)
  const [openChequeDescripcion, setOpenChequeDescripcion] = useState(false)

  const [topItemsVentas, setTopItemsVentas] = useState([])
  // AFIP data for modal reuse
  const [afipVentaData, setAfipVentaData] = useState(null)
  const [afipVentaPayload, setAfipVentaPayload] = useState(null)


  // Función para obtener impuestos válidos para un item específico (igual que en gestionGenerarPresupuesto)
  const getImpuestosValidosParaItem = (itemId, impuestoActual = null) => {
    const impuestosAFIP = getIvaAFIP();

    // Si hay un impuesto actual configurado para este item
    if (impuestoActual && impuestoActual.porcentaje !== undefined) {
      // Buscar si existe un impuesto de AFIP con el mismo porcentaje
      const impuestoAFIPCoincidente = impuestosAFIP.find(imp =>
        Math.abs(imp.porcentaje - impuestoActual.porcentaje) < 0.01
      );

      // Si encontramos un impuesto de AFIP con el mismo porcentaje, usar ese
      if (impuestoAFIPCoincidente) {
        return impuestosAFIP;
      }

      // Si no hay coincidencia, agregar el impuesto personalizado
      return [
        {
          id: impuestoActual.idImpuesto || `custom_${impuestoActual.porcentaje}`,
          descripcion: impuestoActual.descripcionImpuesto || `Impuesto ${impuestoActual.porcentaje}%`,
          porcentaje: impuestoActual.porcentaje,
          codigoAFIP: "CUSTOM"
        },
        ...impuestosAFIP
      ];
    }

    // Retornar solo los impuestos válidos de AFIP
    return impuestosAFIP;
  };

  // Efecto para cargar transacciones de presupuesto a venta
  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        console.log("Entro el id de la carga ")
        setLoading(true)
        try {
          // Filtra las transacciones por ID
          const filteredTransacciones = await getTransaccionFilter(Number(id))

          if (filteredTransacciones) {
            const filteredEntidadesTransaccion = await getEntidadByID(filteredTransacciones.idEntidad)
            console.log("Entidad de transacción:", filteredEntidadesTransaccion)
            setTempValorDescuento(filteredTransacciones.montoDescuento)
            setTempPorcentajeDescuento(filteredTransacciones.porcentajeDescuento)

            setDniCliente({
              label: `${filteredEntidadesTransaccion?.descripcion} ${filteredEntidadesTransaccion?.apellido} ${filteredEntidadesTransaccion?.dniCuitCuil}`,
              descripcion: filteredEntidadesTransaccion?.descripcion || null,
              dniCuitCuil: filteredEntidadesTransaccion?.dniCuitCuil,
              id: filteredEntidadesTransaccion?.id,
              apellido: filteredEntidadesTransaccion?.apellido,
              telefono: filteredEntidadesTransaccion?.telefono || null,
              email: filteredEntidadesTransaccion?.email || null,
              direccion: filteredEntidadesTransaccion?.direccion || null,
              localidad: filteredEntidadesTransaccion?.localidad || null,
              provincia: filteredEntidadesTransaccion?.provincia || null,
              idTipoEntidad: filteredEntidadesTransaccion?.idTipoEntidad,
              // condicionIva: filteredEntidadesTransaccion?.condicionIva || null, // Temporalmente comentado
            })
          }

          // Obtener ítems de transacción por ID
          const filteredItems = await getTransaccionItemsByTransactionId(Number(id))

          // ✅ Filtrar solo items no eliminados para mostrar en itemList
          const filteredItemsActivos = filteredItems.filter(item => !item.eliminado)

          // Manejo asíncrono del mapeo (solo items activos)
          const mappedItems = await Promise.all(
            filteredItemsActivos.map(async (filteredItem) => {
              const ubicacionItem = await getItemUbicacionFilterByUbicacion(
                filteredItem.idItem,
                filteredTransacciones.idUbicacion,
              )

              const precioListaItem = await getListaMontosByIdItemIdEntidad(
                filteredTransacciones.idEntidad,
                filteredItem.idItem,
                filteredTransacciones.idUbicacion,
              )

              // Obtener impuesto del item de forma segura (sin try-catch que rompa el flujo)
              let itemImpuesto = null
              try {
                itemImpuesto = await getImpuestoItem(filteredItem.idItem)
                console.log("Impuesto del item:", itemImpuesto)

                // Guardar el impuesto original en el estado (sin modificar)
                setItemImpuestos(prev => ({
                  ...prev,
                  [filteredItem.idItem]: itemImpuesto
                }))
              } catch (error) {
                console.log("No se encontró impuesto específico para el item:", filteredItem.idItem)
                // Si falla, usar el IVA por defecto sin romper el flujo
                itemImpuesto = {
                  idItem: filteredItem.idItem,
                  idImpuesto: null,
                  porcentaje: 21,
                  descripcionImpuesto: "IVA General"
                }

                // También guardar este impuesto por defecto
                setItemImpuestos(prev => ({
                  ...prev,
                  [filteredItem.idItem]: itemImpuesto
                }))
              }


              console.log("precioListaItem ", precioListaItem)

              const precioItem = precioListaItem ? precioListaItem.monto : 0

              const itemData = await getItemById(filteredItem.idItem)

              // Obtener el impuesto del item para mostrar en la tabla
              let impuestoItem = null

              

              // Buscar si el impuesto actual coincide con algún impuesto de AFIP
              const itemIvaPercentage = itemImpuesto?.porcentaje || 21
              const impuestosAFIP = getIvaAFIP();
              const impuestoAFIPCoincidente = impuestosAFIP.find(imp =>
                Math.abs(imp.porcentaje - itemIvaPercentage) < 0.01
              );

              return {
                ...filteredItem,
                descripcion: itemData?.descripcion || "Descripción no disponible",
                codigo: itemData?.codigo || "Código no disponible",
                stock: ubicacionItem[0].inventario || "No disponible",
                precio: precioItem || "No disponible",
                porcentajeDescuento: filteredItem.porcentajeDescuento || 0,
                porcentajeInteres: filteredItem.porcentajeInteres || 0,
                idItem: itemData.id,
                idUbicacion: ubicacionItem[0].idUbicacion,
                impuestoData: {
                  idImpuesto: impuestoAFIPCoincidente?.id || itemImpuesto?.idImpuesto || null,
                  porcentaje: itemIvaPercentage,
                  descripcionImpuesto: impuestoAFIPCoincidente?.descripcion || itemImpuesto?.descripcionImpuesto || "IVA General",
                  idIva: impuestoAFIPCoincidente?.idIva || 1,
                }
              }
            }),
          )

          // Configurar la ubicación y negocio del presupuesto
          if (filteredTransacciones && filteredTransacciones.idUbicacion) {
            const presupuestoUbicacion = ubicaciones.find((it) => it.id === filteredTransacciones.idUbicacion)
            if (presupuestoUbicacion) {
              setSelectedUbicacion({
                id: presupuestoUbicacion.id,
                label: presupuestoUbicacion.descripcion,
              })
            } else {
              // Fallback a ubicación por defecto si no se encuentra
              const defaultUbicacion = ubicaciones.find((it) => it.id === 1)
              if (defaultUbicacion) {
                setSelectedUbicacion({
                  id: defaultUbicacion.id,
                  label: defaultUbicacion.descripcion,
                })
              }
            }
          } else {
            // Si no hay ubicación en el presupuesto, usar la por defecto
            const defaultUbicacion = ubicaciones.find((it) => it.id === 1)
            if (defaultUbicacion) {
              setSelectedUbicacion({
                id: defaultUbicacion.id,
                label: defaultUbicacion.descripcion,
              })
            }
          }
          
          // Configurar el negocio del presupuesto
          if (filteredTransacciones && filteredTransacciones.idNegocio) {
            const presupuestoNegocio = negocios.find((it) => it.id === filteredTransacciones.idNegocio)
            if (presupuestoNegocio) {
              setSelectedNegocio({
                id: presupuestoNegocio.id,
                label: presupuestoNegocio.descripcion,
              })
            } else {
              // Fallback a negocio por defecto si no se encuentra
              const defaultNegocio = negocios.find((it) => it.id === 1)
              if (defaultNegocio) {
                setSelectedNegocio({
                  id: defaultNegocio.id,
                  label: defaultNegocio.descripcion,
                })
              }
            }
          } else {
            // Si no hay negocio en el presupuesto, usar el por defecto
            const defaultNegocio = negocios.find((it) => it.id === 1)
            if (defaultNegocio) {
              setSelectedNegocio({
                id: defaultNegocio.id,
                label: defaultNegocio.descripcion,
              })
            }
          }
          
          // Configurar el usuario de la transacción original
          if (filteredTransacciones && filteredTransacciones.idUsuario && usuarios.length > 0) {
            const usuarioTransaccion = usuarios.find((user) => user.id === filteredTransacciones.idUsuario)
            if (usuarioTransaccion) {
              setUsuarioSeleccionado({
                label: `${usuarioTransaccion.usuario} ${usuarioTransaccion.nombre || ""} ${usuarioTransaccion.apellido || ""}`,
                ...usuarioTransaccion,
              })
              console.log("✅ Usuario de la transacción seteado:", usuarioTransaccion)
            }
          }

          // 💳 Cargar medios de pago de la transacción
          if (filteredTransacciones.transaccionPago && filteredTransacciones.transaccionPago.length > 0) {
            console.log("📋 Medios de pago de la transacción:", filteredTransacciones.transaccionPago)

            // Verificar si hay un solo medio de pago o múltiples
            if (filteredTransacciones.transaccionPago.length === 1) {
              // 💳 Un solo medio de pago
              const transaccionPagoData = filteredTransacciones.transaccionPago[0]
              const pagoData = transaccionPagoData.pago
              const medioDePagoData = pagoData?.medioDePago
              const tipoMedioDePagoData = medioDePagoData?.tipoMedioDePago

              console.log("💳 Medio de pago individual:", {
                medioDePago: medioDePagoData,
                tipoMedioDePago: tipoMedioDePagoData,
                monto: pagoData?.montoTotal
              })

              // Setear el tipo de medio de pago
              if (tipoMedioDePagoData) {
                const tipoMedioDePagoEncontrado = tipoMedioDePago.find(
                  (tipo) => tipo.id === tipoMedioDePagoData.id
                )
                if (tipoMedioDePagoEncontrado) {
                  setSelectedTipoMedioDePago(tipoMedioDePagoEncontrado)
                  
                  // 🔄 Configurar visibilidad del collapse según el tipo
                  if (tipoMedioDePagoEncontrado.id === 7) {
                    // Múltiple: Mostrar modal
                    setOpenCollapse(false)
                    setMostrarButtonModalMedioDePago(true)
                  } else {
                    // Individual: Mostrar collapse
                    setOpenCollapse(true)
                    setMostrarButtonModalMedioDePago(false)
                  }
                  
                  console.log("✅ Tipo de medio de pago seteado:", tipoMedioDePagoEncontrado)
                }
              }

              // Setear el medio de pago específico
              if (medioDePagoData && tipoMedioDePagoData) {
                try {
                  // Obtener la lista de medios de pago del tipo específico
                  const mediosDePagoDelTipo = await getMedioDePagoIdTipoEspecifico({ id: tipoMedioDePagoData.id })
                  
                  if (mediosDePagoDelTipo) {
                    // Buscar el medio de pago específico en la lista
                    const medioDePagoEncontrado = mediosDePagoDelTipo.find(
                      (medio) => medio.id === medioDePagoData.id
                    )
                    
                    if (medioDePagoEncontrado) {
                      setSelectedMedioDePago(medioDePagoEncontrado)
                      console.log("✅ Medio de pago específico seteado:", medioDePagoEncontrado)
                    } else {
                      // Si no se encuentra en la lista cargada, crear el objeto manualmente
                      setSelectedMedioDePago({
                        id: medioDePagoData.id,
                        descripcion: medioDePagoData.descripcion,
                        idTipoMedioDePago: tipoMedioDePagoData.id
                      })
                      console.log("✅ Medio de pago seteado manualmente:", medioDePagoData)
                    }
                  }
                } catch (error) {
                  console.error("Error al cargar medios de pago específicos:", error)
                  // Fallback: setear manualmente
                  setSelectedMedioDePago({
                    id: medioDePagoData.id,
                    descripcion: medioDePagoData.descripcion,
                    idTipoMedioDePago: tipoMedioDePagoData.id
                  })
                }
              }
            } else {
              // 💳💳💳 Múltiples medios de pago
              console.log("💳💳💳 Múltiples medios de pago detectados:", filteredTransacciones.transaccionPago.length)

              // Setear el tipo como "Múltiple" (id = 7)
              const tipoMedioPagoMultiple = tipoMedioDePago.find((tipo) => tipo.id === 7)
              if (tipoMedioPagoMultiple) {
                setSelectedTipoMedioDePago(tipoMedioPagoMultiple)
                console.log("✅ Tipo medio de pago múltiple seteado")
              }

              // Mapear todos los medios de pago a la lista
              const mediosDePagoMapeados = filteredTransacciones.transaccionPago.map((tp) => {
                const pagoData = tp.pago
                const medioDePagoData = pagoData?.medioDePago
                const tipoMedioDePagoData = medioDePagoData?.tipoMedioDePago

                return {
                  id: medioDePagoData?.id,
                  descripcion: medioDePagoData?.descripcion,
                  monto: pagoData?.montoTotal || 0,
                  tipoMedioDePago: tipoMedioDePagoData?.id,
                  tipoMedioDePagoDescripcion: tipoMedioDePagoData?.descripcion
                }
              })

              setMedioDePagoList(mediosDePagoMapeados)
              console.log("✅ Lista de medios de pago múltiples seteada:", mediosDePagoMapeados)
            }
          } else {
            console.log("⚠️ No se encontraron medios de pago en la transacción")
          }

          // Actualiza el estado con los datos mapeados
          setItemList(mappedItems)
          setFilteredTransaccionItems(mappedItems)
          setCantidadItemsInvocados(mappedItems.length)
          setCantidadItemsSelect()
          updateTotals(mappedItems)
        } catch (error) {
          console.error("Error al obtener datos:", error)
          showSnackbar("Error al cargar los datos de la venta", "error")
        } finally {
          setLoading(false)
        }
      }
    }

    fetchData()
  }, [id])

  // Cada vez que itemList cambie, inicializamos el string a partir de la cantidad numérica
  useEffect(() => {
    const init = {};
    itemList.forEach((it) => {
      const key = `${it.idItem}-${it.idUbicacion}`;
      init[key] = it.cantidad.toString();
    });
    setCantidadInputs(init);
  }, [itemList])

  // Efecto para cargar usuario por defecto
  useEffect(() => {
    const idUsuarioGuardado = Cookies.get("idUsuario")
    if (idUsuarioGuardado && usuarios.length > 0) {
      const usuarioDefault = usuarios.find((user) => user.id === Number.parseInt(idUsuarioGuardado, 10))
      if (usuarioDefault) {
        setUsuarioSeleccionado({
          label: `${usuarioDefault.usuario} ${usuarioDefault.nombre || ""} ${usuarioDefault.apellido || ""}`,
          ...usuarioDefault,
        })
      }
    }
  }, [usuarios])

  // Efecto para reaccionar al cambio de cliente (entidad) seleccionado
  useEffect(() => {
    if (dniCliente && dniCliente.id) {
      console.log("Entidad cliente seleccionada:", dniCliente)
      // Aquí podrías agregar lógica extra para actualizar la búsqueda de ítems,
      // por ejemplo, filtrando por el id de la entidad
    }
  }, [dniCliente])

  const getItemSubtotal = (item) =>
    (Number(item?.cantidad) || 0) * (Number(item?.precio) || 0)

  const getItemDiscountAmount = (item) =>
    getItemSubtotal(item) * ((Number(item?.porcentajeDescuento) || 0) / 100)

  const getItemSubtotalAfterDiscount = (item) =>
    getItemSubtotal(item) - getItemDiscountAmount(item)

  const getItemInterestAmount = (item) =>
    getItemSubtotalAfterDiscount(item) * ((Number(item?.porcentajeInteres) || 0) / 100)

  const getItemLineTotal = (item) =>
    getItemSubtotalAfterDiscount(item) + getItemInterestAmount(item)

  // Efecto para resetear descuentos cuando se desmarca la opción
  useEffect(() => {
    if (aplicarDescuento == false) {
      setTempValorDescuento(0)
      setTempPorcentajeDescuento(0)

      // ✅ Resetear descuentos de todos los items a 0
      //limpiarDescuentosItems()

      const updatedTotal = itemList.reduce((total, item) => total + getItemLineTotal(item), 0)

      setPrecioTotal(truncateToTwoDecimals(updatedTotal))
    }
  }, [aplicarDescuento, itemList])

  // Efecto para resetear interés cuando se desmarca la opción
  useEffect(() => {
    if (aplicarInteres == false) {
      setTempPorcentajeInteres(0)
      const updatedTotal = itemList.reduce((total, item) => total + getItemLineTotal(item), 0)
      setPrecioTotal(truncateToTwoDecimals(updatedTotal))
    }
  }, [aplicarInteres, itemList])

  // Efecto para actualizar totales
  useEffect(() => {
    if (id) {
      updateTotals(filteredTransaccionItems)
    } else {
      updateTotals(itemList)
    }
  }, [itemList, filteredTransaccionItems])

  // Función para actualizar totales
  const updateTotals = (items) => {
    const subtotal = items.reduce((total, item) => total + getItemSubtotal(item), 0)
    const updatedTotal = items.reduce((total, item) => total + getItemLineTotal(item), 0)

    setPrecioBaseTotal(truncateToTwoDecimals(subtotal))
    setPrecioTotal(truncateToTwoDecimals(updatedTotal))
  }

  // Efecto para manejar medios de pago
  useEffect(() => {
    if (selectedTipoMedioDePago) {
      // 🔍 Verificar si el tipo de medio de pago realmente cambió (no solo se cargó inicialmente)
      const tipoHaCambiado = prevTipoMedioDePagoRef.current && 
                             prevTipoMedioDePagoRef.current.id !== selectedTipoMedioDePago.id
      
      if (selectedTipoMedioDePago.id === 7) {
        // 💳💳💳 MÚLTIPLE: Mostrar botón, ocultar collapse
        setOpenCollapse(false)
        setMostrarButtonModalMedioDePago(true)
        
        // 🔓 Abrir modal SOLO si el usuario cambió manualmente (no en carga inicial)
        if (tipoHaCambiado) {
          setOpenModalMedioPago(true)
          console.log("🔄 Usuario cambió a múltiple - Abriendo modal")
        }
        
        // 🧹 Limpiar medio de pago individual SOLO si cambió de tipo
        if (tipoHaCambiado) {
          setSelectedMedioDePago(null)
          console.log("🔄 Limpiando medio de pago individual")
        }
      } else {
        // 💳 INDIVIDUAL: Mostrar collapse, ocultar modal
        setMedioDePagoEspecificoSeleccionadoModal("")
        getMedioDePagoIdTipoEspecifico(selectedTipoMedioDePago)
        setModalMedioDePagoSeleccionado("")
        setMostrarButtonModalMedioDePago(false)
        setOpenCollapse(true)
        
        // 🧹 Limpiar lista múltiple y medio de pago anterior SOLO si cambió de tipo
        if (tipoHaCambiado) {
          setMedioDePagoList([])
          setSelectedMedioDePago(null)
          console.log("🔄 Cambiado a individual - Limpiando lista múltiple y medio de pago anterior")
        }
      }
      
      // 📝 Actualizar la referencia con el tipo actual
      prevTipoMedioDePagoRef.current = selectedTipoMedioDePago
    } else {
      setOpenCollapse(false)
      setOpenModalMedioPago(false)
      setMedioDePagoList([])
      prevTipoMedioDePagoRef.current = null
    }

    if (modalTipoMedioDePagoSeleccionado) {
      getMedioDePagoIdTipoEspecifico(modalTipoMedioDePagoSeleccionado)
      setModalMedioDePagoSeleccionado("")
      setMedioDePagoEspecificoSeleccionadoModal("")
    }
  }, [selectedTipoMedioDePago, modalTipoMedioDePagoSeleccionado])

  // Efecto para búsqueda de ítems
  useEffect(() => {
    buscarItem()
  }, [searchText])

  /**
   * Función para detectar códigos de balanza escritos manualmente
   *
   * FORMATO DEL CÓDIGO DE BALANZA:
   * - Prefijo: 2 dígitos (siempre "20")
   * - PLU: 5 dígitos (código del producto en la balanza)
   * - Peso: 5 dígitos (peso en gramos, ej: 00500 = 0.5kg)
   * - Control: 1 dígito (dígito verificador)
   *
   * EJEMPLO: 20012340050001
   * - Prefijo: 20 (indica que es código de balanza)
   * - PLU: 01234 (código del producto en la balanza)
   * - Peso: 00500 (500 gramos = 0.5kg)
   * - Control: 1 (dígito verificador)
   *
   * NOTA: Los PLUs de balanza en la base de datos NO tienen ceros a la izquierda,
   * pero en el código de barras SÍ los tienen. Esta función maneja ambos casos.
   *
   * USO:
   * 1. Escribir o escanear código de balanza en el campo "Buscar producto"
   * 2. El sistema detecta automáticamente que es código de balanza
   * 3. Se establece la cantidad como el peso en kg
   * 4. Se busca y auto-selecciona el producto
   * 5. Hacer clic en el botón "Agregar" (con icono de carrito) para agregarlo
   *
   * @param {string} codigo - El código completo escaneado o escrito
   * @returns {object|null} - Objeto con información del código de balanza o null si no es válido
   */
  const detectarCodigoBalanza = (codigo) => {
    // Verificar si es un código de balanza (empieza con "20" y tiene al menos 12 dígitos)
    if (codigo && codigo.startsWith("20") && codigo.length >= 12 && /^\d+$/.test(codigo)) {
      // Es un código de balanza: formato 2-5-5 + 1 dígito de control
      // Ejemplo: 20012340050001 = prefijo(20) + PLU(01234) + peso(00500) + control(1)
      const pluBalanza = codigo.substring(2, 7) // 5 dígitos del PLU
      const pesoEnGramos = Number.parseInt(codigo.substring(7, 12)) // 5 dígitos del peso
      const pesoEnKg = pesoEnGramos / 1000 // Convertir gramos a kilogramos

      console.log(`Código de balanza detectado manualmente: PLU=${pluBalanza}, Peso=${pesoEnKg}kg`)

      // Establecer la cantidad como el peso en kg
      setCantidadTransaccionItem(pesoEnKg)

      return {
        pluBalanza,
        pesoEnKg,
        esCodigoBalanza: true,
      }
    }
    return null
  }

  /**
   * Función principal de búsqueda de productos
   *
   * FLUJO DE BÚSQUEDA:
   * 1. Si el texto tiene menos de 3 caracteres: mostrar top 10 productos
   * 2. Si el texto tiene 3+ caracteres: iniciar búsqueda
   *
   * FUNCIONALIDADES COEXISTENTES:
   *
   * A) PRODUCTOS NORMALES (codigoScanner):
   *    - Ejemplo: Lata de arvejas, cajas, productos envasados
   *    - Se escanean códigos de barras normales
   *    - Cantidad = 1 (se edita manualmente si se compran más)
   *    - Se busca en el campo codigoScanner
   *
   * B) PRODUCTOS DE BALANZA (pluBalanza):
   *    - Ejemplo: Bizcochos, frutas, productos a granel
   *    - Se escanean códigos que empiezan con "20"
   *    - Cantidad = peso en kg (se establece automáticamente)
   *    - Se busca en el campo pluBalanza
   *
   * DETECCIÓN AUTOMÁTICA:
   * - Si empieza con "20" y tiene 12+ dígitos = código de balanza
   * - Si no empieza con "20" = código de scanner normal
   * - Ambas funcionalidades funcionan simultáneamente
   */
  const buscarItem = async () => {
    if (searchText.length >= 3) {
      arrayItemTresLetras([])

      // PASO 1: Verificar si es un código de balanza escrito manualmente
      const codigoBalanza = detectarCodigoBalanza(searchText)

      if (codigoBalanza) {
        // ESCENARIO A: Código de balanza detectado manualmente
        console.log(`Buscando producto de balanza con PLU: ${codigoBalanza.pluBalanza}`)

        // Buscar el producto por PLU de balanza usando el backend
        // IMPORTANTE: Enviar solo el PLU extraído con prefijo "PLU:", no el código completo
        // Esto permite que el backend busque directamente en el campo pluBalanza
        const resultado = await getItemTresLetras(`PLU:${codigoBalanza.pluBalanza}`)

        if (resultado && resultado.items && resultado.items.length > 0) {
          // Producto encontrado: establecer cantidad como el peso en kg
          setCantidadTransaccionItem(codigoBalanza.pesoEnKg)
          console.log(`Producto de balanza encontrado. Peso: ${codigoBalanza.pesoEnKg}kg`)

          // Auto-seleccionar el primer producto encontrado para facilitar la adición
          const primerProducto = resultado.items[0]
          setSelectedItem({
            label: generateItemLabel(primerProducto),
            descripcion: primerProducto.descripcion,
            codigo: primerProducto.codigo,
            id: primerProducto.id,
            codigoScanner: primerProducto.codigoScanner,
            pluBalanza: primerProducto.pluBalanza,
            usaGestionLotes: primerProducto.usaGestionLotes || false,
          })
        } else {
          console.log(`No se encontró producto con PLU de balanza: ${codigoBalanza.pluBalanza}`)
          console.log("Verificar que el producto tenga el campo pluBalanza configurado correctamente")
        }

        setScannerActivado(resultado)
      } else {
        // ESCENARIO B: Búsqueda normal (no es código de balanza)
        const resultado = await getItemTresLetras(searchText)

        if (resultado && resultado.buscadoPorScanner) {
          // Si es un producto escaneado (scanner o balanza), establecer cantidad
          if (resultado.buscadoPorBalanza && resultado.pesoBalanza) {
            // Producto de balanza escaneado: la cantidad es el peso en kg
            setCantidadTransaccionItem(resultado.pesoBalanza)
            console.log(`Producto de balanza detectado. Peso: ${resultado.pesoBalanza}kg`)
          } else {
            // Producto escaneado normal: cantidad 1
            setCantidadTransaccionItem(1)
          }
        }

        setScannerActivado(resultado)
      }
    } else {
      // Texto insuficiente: mostrar top 10 productos
      arrayItemTresLetras([])
      arrayItemTresLetras(topVentasItems)
      setFilteredItems([])
    }
  }

  useEffect(() => {
    console.log("scannerActivado: ", scannerActivado)
    if (scannerActivado) {
      agregarItemPorScanner()
    }
  }, [scannerActivado])

  // Sincronizar precios formateados cuando cambian los items
  useEffect(() => {
    const newPrecioInputs = {}
    itemList.forEach((it) => {
      const rowKey = `${it.idItem}-${it.idUbicacion}`
      if (!precioInputs[rowKey] || precioInputs[rowKey] === "") {
        newPrecioInputs[rowKey] = formatInputWithSeparators(it.precio || 0, localeConfig.locale)
      }
    })
    if (Object.keys(newPrecioInputs).length > 0) {
      setPrecioInputs((prev) => ({ ...prev, ...newPrecioInputs }))
    }
  }, [itemList, localeConfig])

  /**
   * Función para agregar productos escaneados (scanner o balanza)
   *
   * ESTA FUNCIÓN MANEJA AMBOS TIPOS DE PRODUCTOS:
   *
   * 1) PRODUCTOS NORMALES (codigoScanner):
   *    - Ejemplo: Lata de arvejas escaneada
   *    - Cantidad = 1 (se estableció en buscarItem)
   *    - Se busca por codigoScanner en la BD
   *    - Funciona exactamente igual que antes
   *
   * 2) PRODUCTOS DE BALANZA (pluBalanza):
   *    - Ejemplo: Bizcochos escaneados desde balanza
   *    - Cantidad = peso en kg (se estableció automáticamente)
   *    - Se busca por pluBalanza en la BD
   *    - Nueva funcionalidad agregada
   *
   * IMPORTANTE: Ambas funcionalidades coexisten y no se interfieren entre sí
   */
  const agregarItemPorScanner = async () => {
    // Si es un producto de balanza, asegurar que la cantidad esté establecida
    if (scannerActivado && scannerActivado.buscadoPorBalanza && scannerActivado.pesoBalanza) {
      console.log(`Agregando producto de balanza con peso: ${scannerActivado.pesoBalanza}kg`)
      // La cantidad ya se estableció en buscarItem() para productos de balanza
    }
    console.log("entro por aca ")

    await handleAddItem()
  }

  // Efecto para búsqueda de clientes (ahora solo actualiza el Autocomplete; la selección se maneja en el bloque nuevo)
  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1)
    } else {
      setFilteredItems([])
    }
  }, [searchTextCliente])

  // Efecto para cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const top10items = await getTop10ItemVentas()
        await getTiposFacturas()
        await getTipoEntidad()
        await getTipoMedioDePago()
        await getUsuario()
        await getImpuestos()
        await getItemAtributoNoEliminados()

        // 🆕 Cargar parámetro global de gestión de lotes
        const parametrosLotes = await getParametrosGlobales(["gestionDeLotes"])
        console.log("📦 Parámetro gestionDeLotes:", parametrosLotes);
        if (parametrosLotes.gestionDeLotes == "1") {
          setGestionDeLotesActiva(true)
          console.log("✅ Gestión de lotes ACTIVADA");
        } else {
          setGestionDeLotesActiva(false)
          console.log("❌ Gestión de lotes DESACTIVADA");
        }

        const fecha = await getFechaHoraActualClient()
        const fechaServidor = fecha.fechaServidor
        console.log("fechaServidor", fechaServidor)
        
        // ✅ Si estamos editando (hay id), desactivar facturar ignorando parámetro global
        // Pero el checkbox sigue siendo editable para poder convertir en factura
        if (id) {
          setCheckboxFacturaNegro(true) // Desactivado (no facturar)
          setAceptarImpuestos(false)
        } else {
          // Solo aplicar parámetro global si NO estamos editando
        const result = await getIncluirImpuestos()
        console.log("Resultado de impuestos:", result)
        if (result.valorParametro == "1") {
          setCheckboxFacturaNegro(true)
          setAceptarImpuestos(true)
        } else {
          setCheckboxFacturaNegro(false)
          setAceptarImpuestos(false)
          }
        }

        if (!id) {
          const defaultEntidad = await getEntidadByID(1)

          if (defaultEntidad) {
            setDniCliente({
              label: `${defaultEntidad.descripcion} ${defaultEntidad.apellido} ${defaultEntidad.dniCuitCuil}`,
              descripcion: defaultEntidad.descripcion || null,
              dniCuitCuil: defaultEntidad.dniCuitCuil,
              id: defaultEntidad.id,
              apellido: defaultEntidad.apellido,
              telefono: defaultEntidad.telefono || null,
              email: defaultEntidad.email || null,
              direccion: defaultEntidad.direccion || null,
              localidad: defaultEntidad.localidad || null,
              provincia: defaultEntidad.provincia || null,
              idTipoEntidad: defaultEntidad.idTipoEntidad,
              // condicionIva: defaultEntidad.condicionIva || null, // Temporalmente comentado
            })
          }
        }

        const ubicacionTraido = await getUbicacion()
        const negocioTraido = await getNegocio()

        if (top10items) {
          // Mapeo para extraer los datos internos del item
          const mappedTop10 = top10items.map(({ item, ventas }) => ({
            id: item.id,
            codigo: item.codigo,
            descripcion: item.descripcion,
            codigoScanner: item.codigoScanner,
            usaGestionLotes: item.usaGestionLotes || false,
          }))

          set10VentasItems(mappedTop10)
          arrayItemTresLetras(mappedTop10)
        }

        // Configurar la ubicación predeterminada solo si NO estamos cargando un presupuesto (no hay id)
        if (!id) {
          const defaultUbicacion = ubicacionTraido.find((it) => it.id === 1)
          const defaultNegocio = negocioTraido.find((it) => it.id === 1)

          if (defaultUbicacion) {
            setSelectedUbicacion({
              id: defaultUbicacion.id,
              label: defaultUbicacion.descripcion,
            })
          }
          if (defaultNegocio) {
            setSelectedNegocio({
              id: defaultNegocio.id,
              label: defaultNegocio.descripcion,
            })
          }
        }

        autocompleteRef.current.focus()

        setLoading(false)
        showSnackbar("Datos cargados correctamente", "success")
      } catch (error) {
        console.error("Error al cargar los datos:", error)
        showSnackbar("Error al cargar los datos iniciales", "error")
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  // Efecto para ajustar altura de la lista
  useEffect(() => {
    const ajustarAlturaLista = () => {
      const contenedorCantidadItem = document.querySelector(".contenedorCantidadItem")
      const managerDiv = document.querySelector(".ManagerDiv")

      if (!managerDiv) return

      if (contenedorCantidadItem && itemList.length > 0) {
        const footerHeight = contenedorCantidadItem.offsetHeight
        managerDiv.style.marginBottom = `${footerHeight}px`
      } else {
        managerDiv.style.marginBottom = "0px"
      }
    }

    ajustarAlturaLista()
    window.addEventListener("resize", ajustarAlturaLista)

    return () => {
      window.removeEventListener("resize", ajustarAlturaLista)
    }
  }, [itemList])

  // Efecto para resetear margen al cambiar de ruta
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    const managerDiv = document.querySelector(".ManagerDiv")
    if (managerDiv) {
      managerDiv.style.marginBottom = "0px"
    }
  }, [location.pathname])

  // Efecto para calcular precios con impuestos
  useEffect(() => {
    // Calcular subtotal SIN descuentos (precio × cantidad)
    const subtotalSinDescuentos = itemList.reduce((total, item) => total + getItemSubtotal(item), 0)

    setPrecioBaseTotal(subtotalSinDescuentos)

    // Calcular el monto total de descuentos aplicados a cada línea
    const totalDescuentos = itemList.reduce((total, item) => total + getItemDiscountAmount(item), 0)
    
    setMontoTotalDescuentos(totalDescuentos)
    const totalIntereses = itemList.reduce((total, item) => total + getItemInterestAmount(item), 0)
    setMontoTotalIntereses(totalIntereses)

    // Calcular total con descuento + interés por línea
    const totalConDescuento = subtotalSinDescuentos - totalDescuentos + totalIntereses

    // Aplicar descuento en valor si existe (esto ya no debería usarse con el nuevo sistema)
    const valorDesc = Number(Number.parseFloat(tempValorDescuento).toFixed(2)) || 0
    
    let totalFinal = totalConDescuento
    
    if (valorDesc > 0) {
      totalFinal -= valorDesc
    }
    
    if (totalFinal < 0) {
      totalFinal = 0
    }

    setPrecioBaseFinalConDescuento(totalFinal)

    if (!checkboxFacturaNegro) {
      if (impuestos.length > 0) {
        // Verificar si los impuestos están incluidos o no
        const impuestosIncluidos = impuestos.every((impuesto) => impuesto.incluidoEnPrecio)

        if (impuestosIncluidos) {
          // Desglosar impuestos si ya están en el precio
          setMostrarIva(true)
          const totalSinImpuestos = impuestos
            .filter((impuesto) => !impuesto.eliminado)
            .reduce((base, impuesto) => base / (1 + impuesto.porcentaje / 100), totalFinal)

          const totalImpuestos = totalFinal - totalSinImpuestos

          setPrecioTotal(truncateToTwoDecimals(totalFinal))
          setPrecioTotalConImpuestos(truncateToTwoDecimals(totalFinal + totalImpuestos))
        } else {
          setMostrarIva(false)

          // Calcular impuestos adicionales si NO están incluidos
          const totalImpuestos = impuestos
            .filter((impuesto) => !impuesto.eliminado)
            .reduce((acumulador, impuesto) => acumulador + (totalFinal * impuesto.porcentaje) / 100, 0)

          setPrecioTotal(truncateToTwoDecimals(totalFinal + totalImpuestos))
          setPrecioTotalConImpuestos(truncateToTwoDecimals(totalFinal + totalImpuestos))
        }
      } else {
        setPrecioTotal(truncateToTwoDecimals(totalFinal))
        setPrecioTotalConImpuestos(truncateToTwoDecimals(totalFinal))
      }
    } else {
      setPrecioTotal(truncateToTwoDecimals(totalFinal))
      setPrecioTotalConImpuestos(truncateToTwoDecimals(totalFinal))
    }
  }, [itemList, impuestos, checkboxFacturaNegro, tempValorDescuento, tempPorcentajeDescuento, tempPorcentajeInteres])

  // Efecto para habilitar/deshabilitar botón de generar venta
  useEffect(() => {
    if (itemList.length > 0 && dniCliente != null && (medioDePagoList.length > 0 || selectedMedioDePago != null)) {
      setIsButtonDisabled(false)
    } else {
      setIsButtonDisabled(true)
    }
  }, [itemList, dniCliente, medioDePagoList, selectedMedioDePago])

  // Efecto para resetear medio de pago seleccionado
  useEffect(() => {
    if (selectedTipoMedioDePago == null) {
      setSelectedMedioDePago("")
    }
  }, [selectedTipoMedioDePago])

  // Efecto para autoseleccionar ítem por código de barras
  useEffect(() => {
    if (itemTresLetras) {
      if (searchText.length >= 3 && itemTresLetras.length > 0) {
        // Verificar si es un código de balanza antes de buscar por codigoScanner
        const esCodigoBalanza = searchText.startsWith("20") && searchText.length >= 12 && /^\d+$/.test(searchText);
        
        if (!esCodigoBalanza) {
          // Solo buscar por codigoScanner si NO es un código de balanza
          const found = itemTresLetras.find((item) => item.codigoScanner === searchText)
          if (found) {
            const autoSelected = {
              label: generateItemLabel(found),
              descripcion: found.descripcion,
              codigo: found.codigo,
              id: found.id,
              codigoScanner: found.codigoScanner,
              usaGestionLotes: found.usaGestionLotes || false,
            }
            setSelectedItem(autoSelected)
            if (cantidadRef.current) {
              buttonRef.current.focus()
            }

            agregarItemPorScanner()
          }
        }
      }
    }
  }, [searchText, itemTresLetras])

  // Función para aplicar descuentos
  const applyDiscounts = () => {
    const porcentajeDesc = Number(Number.parseFloat(tempPorcentajeDescuento).toFixed(2)) || 0

    // Sumar o restar descuentos a cada item
    if (itemList.length > 0) {
      const updatedItemList = itemList.map(item => {
        const descuentoActual = item.porcentajeDescuento || 0
        let nuevoDescuento = descuentoActual + porcentajeDesc
        
        // Validación: Máximo 100%, mínimo 0%
        if (nuevoDescuento > 100) {
          nuevoDescuento = 100
        }
        if (nuevoDescuento < 0) {
          nuevoDescuento = 0
        }
        
        return {
          ...item,
          porcentajeDescuento: nuevoDescuento
        }
      })

      setItemList(updatedItemList)
      console.log('Descuento general sumado a todos los items:', porcentajeDesc + '%')

      // Resetear el input después de aplicar
      setTempPorcentajeDescuento("0")
      
      // Forzar re-renderizado
      setTimeout(() => {
        setItemList([...updatedItemList])
      }, 0)
    }
  }

  // ✅ Función para limpiar descuentos de todos los items
  const limpiarDescuentosItems = () => {
    if (itemList.length > 0) {
      const updatedItemList = itemList.map(item => ({
        ...item,
        porcentajeDescuento: 0
      }))
      setItemList(updatedItemList)
      setTempPorcentajeDescuento("0")
      console.log('Descuentos de items limpiados')

      // ✅ Forzar re-renderizado para que los campos se actualicen
      setTimeout(() => {
        setItemList([...updatedItemList])
      }, 0)
    }
  }

  // Función para aplicar interés general a todos los ítems
  const applyInterest = () => {
    const porcentajeInt = Number(Number.parseFloat(tempPorcentajeInteres).toFixed(2)) || 0

    if (itemList.length > 0) {
      const updatedItemList = itemList.map(item => {
        const interesActual = item.porcentajeInteres || 0
        let nuevoInteres = interesActual + porcentajeInt

        if (nuevoInteres > 100) {
          nuevoInteres = 100
        }
        if (nuevoInteres < 0) {
          nuevoInteres = 0
        }

        return {
          ...item,
          porcentajeInteres: nuevoInteres,
        }
      })

      setItemList(updatedItemList)
      setTempPorcentajeInteres("0")
      console.log("Interés general sumado a todos los items:", porcentajeInt + "%")

      setTimeout(() => {
        setItemList([...updatedItemList])
      }, 0)
    }
  }

  // ✅ Función para limpiar intereses de todos los items
  const limpiarInteresesItems = () => {
    if (itemList.length > 0) {
      const updatedItemList = itemList.map(item => ({
        ...item,
        porcentajeInteres: 0,
      }))
      setItemList(updatedItemList)
      setTempPorcentajeInteres("0")
      console.log("Intereses de items limpiados")

      setTimeout(() => {
        setItemList([...updatedItemList])
      }, 0)
    }
  }

  // Función para agregar ítem a la lista
  const handleAddItem = async () => {
    try {
      if (selectedItem) {

        // Obtener el impuesto del item de forma segura
        let itemImpuesto = null
        try {
          itemImpuesto = await getImpuestoItem(selectedItem.id)
          console.log("Impuesto del item:", itemImpuesto)

          // Guardar el impuesto en el estado
          setItemImpuestos(prev => ({
            ...prev,
            [selectedItem.id]: itemImpuesto
          }))
        } catch (error) {
          console.log("No se encontró impuesto específico para el item:", selectedItem.id)
          // Si falla, usar el IVA por defecto sin romper el flujo
          itemImpuesto = {
            idItem: selectedItem.id,
            idImpuesto: null,
            porcentaje: 21,
            descripcionImpuesto: "IVA General"
          }
        }


        const ubicacionItem = await getItemUbicacionFilterByUbicacion(selectedItem.id, selectedUbicacion.id)

        const precioListaItem = await getListaMontosByIdItemIdEntidad(
          dniCliente.id,
          selectedItem.id,
          selectedUbicacion.id,
        )

        console.log("precioListaItem ", precioListaItem)

        const stockDisponible = ubicacionItem ? ubicacionItem[0].inventario : "No disponible"
        const precioItem = precioListaItem ? precioListaItem.monto : 0

        if (stockDisponible === "No disponible" || precioItem === "No disponible") {
          showSnackbar("Producto no encontrado", "error")
          return
        }

        const existingItemIndex = itemList.findIndex(
          (it) => it.idItem === selectedItem.id && it.idUbicacion === selectedUbicacion.id,
        )

        console.log("existingItemIndex", existingItemIndex)

        if (existingItemIndex !== -1) {
          // Usar cantidadTransaccionItem si está disponible (para productos de balanza), sino usar 1
          const cantidadASumar = cantidadTransaccionItem && Number(cantidadTransaccionItem) > 0 
            ? Number(cantidadTransaccionItem) 
            : 1;
            
          await handleUpdateItem(
            existingItemIndex,
            undefined,
            Number(itemList[existingItemIndex].cantidad) + cantidadASumar,
            undefined,
          )
        } else {
          // Obtener el impuesto del item para mostrar en la tabla
          let impuestoItem = null

          // Buscar si el impuesto actual coincide con algún impuesto de AFIP
          const itemIvaPercentage = !checkboxFacturaNegro ? (itemImpuesto?.porcentaje || 21) : 0
          const impuestosAFIP = await getIvaAFIP();
          const impuestoAFIPCoincidente = impuestosAFIP.find(imp =>
            Math.abs(imp.porcentaje - itemIvaPercentage) < 0.01
          );

          // Usar cantidadTransaccionItem si está disponible (para productos de balanza), sino usar 1
          const cantidadAUsar = cantidadTransaccionItem && Number(cantidadTransaccionItem) > 0 
            ? Number(cantidadTransaccionItem) 
            : 1;

          // 🆕 Variable para almacenar el stock a usar (de lotes o de ItemUbicacion)
          let stockParaMostrar = stockDisponible;

          // 🆕 Si el parámetro global está activo Y el item usa gestión de lotes
          console.log("🔍 Verificando gestión de lotes:", {
            gestionDeLotesActiva,
            usaGestionLotes: selectedItem.usaGestionLotes,
            itemId: selectedItem.id
          });
          
          if (gestionDeLotesActiva && selectedItem.usaGestionLotes) {
            console.log("📦 Item usa lotes, obteniendo sugerencia FIFO...");
            try {
              const respuesta = await getSugerenciaLotesVenta(
                selectedItem.id,
                selectedUbicacion.id,
                cantidadAUsar
              );
              
              console.log("📦 Respuesta de sugerencia:", respuesta);
              
              // 🆕 Backend ahora devuelve objeto con { lotes, stockTotal, cantidadRequerida, stockSuficiente }
              const sugerencia = respuesta?.lotes || respuesta;
              const stockSuficiente = respuesta?.stockSuficiente !== undefined ? respuesta.stockSuficiente : true;
              // 🆕 Usar stockTotal de los lotes en lugar del stock de ItemUbicacion
              stockParaMostrar = respuesta?.stockTotal || stockDisponible;
              
              // Guardar los lotes para este item
              const lotesParaEsteItem = sugerencia.map(lote => ({
                idLote: lote.idLote,
                numeroLote: lote.numeroLote,
                cantidad: lote.cantidadAsignada,
                fechaVencimiento: lote.fechaVencimiento,
                fechaFabricacion: lote.fechaFabricacion,
                stockDisponible: lote.stockDisponible
              }));
              
              setLotesDelCarrito(prev => {
                const nuevo = {
                  ...prev,
                  [selectedItem.id]: lotesParaEsteItem
                };
                console.log("✅ Estado lotesDelCarrito actualizado:", nuevo);
                return nuevo;
              });
              
              console.log("✅ Sugerencia FIFO obtenida para item", selectedItem.id, ":", lotesParaEsteItem);
              
              // 🆕 Mostrar advertencia si el stock es insuficiente
              if (!stockSuficiente) {
                const totalAsignado = lotesParaEsteItem.reduce((sum, lote) => sum + lote.cantidad, 0);
                showSnackbar(
                  `⚠️ Stock insuficiente: disponible ${totalAsignado} de ${cantidadAUsar} unidades`, 
                  "warning"
                );
              }
            } catch (error) {
              console.error("❌ Error al obtener sugerencia de lotes:", error);
              console.error("❌ Detalles del error:", error.response?.data);
              showSnackbar("Error: " + (error.response?.data?.message || "No hay lotes disponibles"), "error");
              // 🆕 Ya no impedimos agregar el item, solo mostramos el error
              // El usuario puede ver el error y decidir
              return;
            }
          } else {
            console.log("ℹ️  Item NO usa lotes o parámetro global desactivado");
          }

          const newItem = {
            descripcion: selectedItem.descripcion,
            cantidad: cantidadAUsar,
            stock: stockParaMostrar, // 🆕 Usar stock de lotes si aplica, sino usar stock de ItemUbicacion
            precio: precioItem,
            codigo: selectedItem.codigo,
            porcentajeDescuento: 0,
            porcentajeInteres: 0,
            idItem: selectedItem.id,
            idUbicacion: selectedUbicacion.id,
            ubicacion: selectedUbicacion.label,
            usaGestionLotes: selectedItem.usaGestionLotes || false, // Guardar si usa lotes
            impuestoData: {
              idImpuesto: impuestoAFIPCoincidente?.id || itemImpuesto?.idImpuesto || null,
              porcentaje: itemIvaPercentage,
              descripcionImpuesto: impuestoAFIPCoincidente?.descripcion || itemImpuesto?.descripcionImpuesto || "IVA General",
              idIva: impuestoAFIPCoincidente?.idIva || 1,
            }
          }

          // Ahora sí actualizar el carrito
          const updatedItems = [newItem, ...itemList]
          const updatedTotal = updatedItems.reduce((total, item) => total + item.cantidad * item.precio, 0)
          const updatedCantidadItem = updatedItems.reduce((cantidadItems, item) => cantidadItems + item.cantidad, 0)

          setPrecioTotal(truncateToTwoDecimals(updatedTotal))
          setCantidadItemsSelect(updatedCantidadItem)
          setPrecioBaseFinalConDescuento(truncateToTwoDecimals(updatedTotal))
          setItemList(updatedItems)
          setCantidadItemsInvocados(updatedItems.length)

          showSnackbar(`Ítem "${selectedItem.descripcion}" agregado`, "success")
        }

        setSelectedItem(null)
        setCantidadTransaccionItem("")
        if (autocompleteRef.current) {
          autocompleteRef.current.focus()
        }
      }
    } catch (error) {
      console.error("Error al agregar el ítem:", error)
      showSnackbar("No se encuentra el ítem en la ubicación seleccionada", "error")
    }
  }

  // Función para calcular IVA detallado por alícuotas para AFIP
  // Ejemplo de resultado:
  // [
  //   { Id: 5, BaseImp: 10000.00, Importe: 2100.00 },    // 21%
  //   { Id: 4, BaseImp:  5000.00, Importe:  525.00 },    // 10,5%
  //   { Id: 3, BaseImp:  2000.00, Importe:     0.00 }    // 0% (si es gravado 0%)
  // ✅ Función para distribuir descuento en valor proporcionalmente por grupos de IVA
  const distribuirDescuentoEnValorPorIva = (items, descuentoEnValor) => {
    if (!descuentoEnValor || descuentoEnValor <= 0) {
      return items // Sin cambios si no hay descuento
    }

    const ivaAFIP = getIvaAFIP()
    const gruposIva = {}

    // Primera pasada: agrupar items por alícuota de IVA y calcular totales
    items.forEach(item => {
      let porcentajeIva = 21 // Default

      if (item.impuestoData && item.impuestoData.porcentaje !== undefined) {
        porcentajeIva = item.impuestoData.porcentaje
      } else if (item.ivaPercentage !== undefined) {
        porcentajeIva = item.ivaPercentage
      }

      // Encontrar el ID de AFIP correspondiente al porcentaje
      const ivaAFIPEncontrado = ivaAFIP.find(iva => Math.abs(iva.porcentaje - porcentajeIva) < 0.01)
      const idAFIP = ivaAFIPEncontrado ? ivaAFIPEncontrado.ivaAFIP : 5 // Default a 21%

      if (!gruposIva[idAFIP]) {
        gruposIva[idAFIP] = {
          id: idAFIP,
          porcentaje: porcentajeIva,
          totalGrupo: 0,
          items: []
        }
      }

      // Calcular total del item (con descuento/interés porcentual ya aplicado)
      const totalItem = getItemLineTotal(item)

      gruposIva[idAFIP].totalGrupo += totalItem
      gruposIva[idAFIP].items.push({
        ...item,
        totalItem,
        idAFIP
      })
    })

    // Calcular total general para distribución proporcional
    const totalGeneral = Object.values(gruposIva).reduce((sum, grupo) => sum + grupo.totalGrupo, 0)

    // Segunda pasada: distribuir descuento en valor proporcionalmente
    const itemsConDescuentoDistribuido = items.map(item => {
      const grupo = Object.values(gruposIva).find(g =>
        g.items.some(i => i.idItem === item.idItem && i.idUbicacion === item.idUbicacion)
      )

      if (grupo) {
        // Calcular proporción del grupo en el total general
        const proporcionGrupo = grupo.totalGrupo / totalGeneral

        // Calcular descuento en valor que le corresponde a este grupo
        const descuentoGrupo = descuentoEnValor * proporcionGrupo

        // Calcular descuento proporcional para este item específico
        const totalItem = getItemLineTotal(item)
        const proporcionItem = totalItem / grupo.totalGrupo
        const descuentoItem = descuentoGrupo * proporcionItem

        // Calcular precio final con descuento en valor aplicado
        const precioFinal = Math.max(0, totalItem - descuentoItem)

        return {
          ...item,
          descuentoEnValorAplicado: descuentoItem,
          precioFinalConDescuentoEnValor: precioFinal
        }
      }

      return item
    })

    console.log('Descuento en valor distribuido por grupos de IVA:', {
      descuentoTotal: descuentoEnValor,
      grupos: Object.values(gruposIva).map(g => ({
        idIva: g.id,
        porcentaje: g.porcentaje,
        totalGrupo: g.totalGrupo,
        descuentoProporcional: descuentoEnValor * (g.totalGrupo / totalGeneral)
      })),
      itemsActualizados: itemsConDescuentoDistribuido
    })

    return itemsConDescuentoDistribuido
  }

  // Función para actualizar el impuesto de un item específico
  const handleUpdateItemImpuesto = (index, newIvaPercentage) => {
    const updatedItems = [...itemList];
    const item = updatedItems[index];

    // Encontrar el impuesto correspondiente al porcentaje seleccionado
    const impuestosValidos = getImpuestosValidosParaItem(item.idItem, item.impuestoData);
    const impuestoSeleccionado = impuestosValidos.find(imp =>
      Math.abs(imp.porcentaje - newIvaPercentage) < 0.01
    );

    // Actualizar el item con el nuevo impuesto
    item.ivaPercentage = newIvaPercentage;
    item.impuestoData = {
      idImpuesto: impuestoSeleccionado?.id || null,
      porcentaje: newIvaPercentage,
      descripcionImpuesto: impuestoSeleccionado?.descripcion || `IVA ${newIvaPercentage}%`,
      idIva: impuestoSeleccionado?.idIva || 1,
    };

    // Recalcular precios
    const precioSinIva = checkboxFacturaNegro
      ? truncateToTwoDecimals(
        (item.precio / (1 + newIvaPercentage / 100)) *
        (1 - (item.porcentajeDescuento || 0) / 100) *
        (1 + (item.porcentajeInteres || 0) / 100),
      )
      : item.precio;
    const montoIva = checkboxFacturaNegro
      ? truncateToTwoDecimals(precioSinIva * (newIvaPercentage / 100))
      : 0;

    item.precioSinIva = precioSinIva;
    item.montoIva = montoIva;

    setItemList(updatedItems);
    setFilteredTransaccionItems(updatedItems);

    // Marcar que este item tiene un impuesto modificado
    setImpuestosModificados(prev => ({
      ...prev,
      [item.idItem]: {
        idImpuesto: impuestoSeleccionado?.id || null,
        porcentajeOriginal: item.impuestoData?.porcentaje || 21,
        porcentajeNuevo: newIvaPercentage,
        descripcion: item.descripcion
      }
    }));

    // Actualizar totales
    updateTotals(updatedItems);
  };


  // ✅ Función para calcular IVA detallado por alícuotas considerando descuento en valor
  const calcularIvaDetalladoPorAlícuotas = (items, descuentoEnValor = 0) => {
    const ivaAFIP = getIvaAFIP()
    const gruposIva = {}

    // Aplicar descuento en valor distribuido si existe
    const itemsConDescuentoDistribuido = descuentoEnValor > 0
      ? distribuirDescuentoEnValorPorIva(items, descuentoEnValor)
      : items

    // Agrupar items por alícuota de IVA
    itemsConDescuentoDistribuido.forEach(item => {
      let porcentajeIva = 21 // Default

      if (item.impuestoData && item.impuestoData.porcentaje !== undefined) {
        porcentajeIva = item.impuestoData.porcentaje
      } else if (item.ivaPercentage !== undefined) {
        porcentajeIva = item.ivaPercentage
      }

      // Encontrar el ID de AFIP correspondiente al porcentaje
      const ivaAFIPEncontrado = ivaAFIP.find(iva => Math.abs(iva.porcentaje - porcentajeIva) < 0.01)
      const idAFIP = ivaAFIPEncontrado ? ivaAFIPEncontrado.ivaAFIP : 5 // Default a 21%

      if (!gruposIva[idAFIP]) {
        gruposIva[idAFIP] = {
          id: idAFIP,
          porcentaje: porcentajeIva,
          baseImponible: 0,
          importeIva: 0,
          items: []
        }
      }

      // Calcular valores del item considerando descuento en valor
      let precioItem = getItemLineTotal(item)

      // Si hay descuento en valor distribuido, aplicarlo
      if (item.descuentoEnValorAplicado) {
        precioItem = item.precioFinalConDescuentoEnValor
      }

      // Calcular base imponible e IVA
      const baseImponibleItem = precioItem / (1 + (porcentajeIva / 100))
      const ivaItem = precioItem - baseImponibleItem

      gruposIva[idAFIP].baseImponible += baseImponibleItem
      gruposIva[idAFIP].importeIva += ivaItem
      gruposIva[idAFIP].items.push({
        ...item,
        baseImponibleItem,
        ivaItem,
        precioFinal: precioItem
      })
    })

    // Convertir a array para AFIP
    const ivaArray = Object.values(gruposIva).map(grupo => ({
      Id: grupo.id,
      BaseImp: Number.parseFloat(grupo.baseImponible.toFixed(2)),
      Importe: Number.parseFloat(grupo.importeIva.toFixed(2))
    }))

    console.log("IVA detallado por alícuotas (con descuento en valor):", ivaArray)
    return ivaArray
  }

  // Función para actualizar un ítem
  const handleUpdateItem = async (index, newDescripcion, newCantidad, newDescuento, newInteres) => {
    const updatedItems = await Promise.all(
      itemList.map(async (it, i) => {
        if (i === index) {
          try {
            // 🆕 Si cambió la cantidad Y el item usa gestión de lotes, recalcular lotes automáticamente (FIFO)
            const cantidadCambio = newCantidad !== undefined && Number(newCantidad) !== it.cantidad;
            let nuevoStock = it.stock; // Mantener stock actual por defecto
            
            if (cantidadCambio && it.usaGestionLotes && gestionDeLotesActiva) {
              try {
                const nuevaCantidad = Number(newCantidad);
                const respuesta = await getSugerenciaLotesVenta(it.idItem, it.idUbicacion, nuevaCantidad);
                
                // 🆕 Backend ahora devuelve objeto con { lotes, stockTotal, cantidadRequerida, stockSuficiente }
                const sugerencia = respuesta?.lotes || respuesta; // Soporte para formato antiguo y nuevo
                const stockSuficiente = respuesta?.stockSuficiente !== undefined ? respuesta.stockSuficiente : true;
                const stockTotal = respuesta?.stockTotal || 0;
                // 🆕 Actualizar stock con el stockTotal de los lotes
                nuevoStock = stockTotal;
                
                if (sugerencia && Array.isArray(sugerencia) && sugerencia.length > 0) {
                  // Actualizar lotes del carrito con la nueva sugerencia FIFO
                  const nuevosLotes = sugerencia.map(lote => ({
                    idLote: lote.idLote,
                    numeroLote: lote.numeroLote,
                    cantidad: lote.cantidadAsignada,
                    fechaFabricacion: lote.fechaFabricacion,
                    fechaVencimiento: lote.fechaVencimiento,
                    stockDisponible: lote.stockDisponible
                  }));
                  
                  setLotesDelCarrito(prev => ({
                    ...prev,
                    [it.idItem]: nuevosLotes
                  }));
                  
                  // 🆕 Mostrar advertencia si el stock es insuficiente
                  if (!stockSuficiente) {
                    const totalAsignado = nuevosLotes.reduce((sum, lote) => sum + lote.cantidad, 0);
                    showSnackbar(
                      `⚠️ Stock insuficiente para "${it.descripcion}": disponible ${totalAsignado} de ${nuevaCantidad} unidades`, 
                      "warning"
                    );
                  }
                } else if (nuevaCantidad > 0) {
                  // Si no hay lotes disponibles pero se pide cantidad, mostrar advertencia
                  showSnackbar(`⚠️ No hay lotes disponibles para "${it.descripcion}"`, "warning");
                }
              } catch (error) {
                console.error(`Error al recalcular lotes para ${it.descripcion}:`, error);
                showSnackbar(`Error al recalcular lotes para "${it.descripcion}"`, "error");
              }
            }
            
            return {
              ...it,
              descripcion: it.descripcion,
              cantidad: newCantidad != undefined ? Number(newCantidad) : it.cantidad,
              stock: nuevoStock, // 🆕 Usar stock actualizado de lotes si aplica
              precio: it.precio,
              codigo: it.codigo,
              porcentajeDescuento: newDescuento !== undefined ? Number(newDescuento) : it.porcentajeDescuento,
              porcentajeInteres: newInteres !== undefined ? Number(newInteres) : (it.porcentajeInteres || 0),
              idItem: it.idItem,
              idUbicacion: it.idUbicacion,
              ubicacion: it.ubicacion,
              impuestoData: it.impuestoData, // Mantener el impuesto del item
            }
          } catch (error) {
            console.error("Error al actualizar el ítem:", error)
            showSnackbar("Error al actualizar el ítem", "error")
          }
        }
        return it
      }),
    )

    const updatedCantidadItem = updatedItems.reduce((cantidadItems, item) => cantidadItems + item.cantidad, 0)
    const updatedTotal = updatedItems.reduce((total, item) => total + getItemLineTotal(item), 0)

    setPrecioTotal(truncateToTwoDecimals(updatedTotal))
    setCantidadItemsSelect(updatedCantidadItem)
    setItemList(updatedItems)

    showSnackbar("Ítem actualizado", "success")
  }

  // Función para eliminar un ítem
  const handleDeleteItem = (index) => {
    const itemToRemove = itemList[index]
    setPrecioTotal((prevTotal) => truncateToTwoDecimals(prevTotal - getItemLineTotal(itemToRemove)))
    setCantidadItemsSelect((prevCantidad) => prevCantidad - itemToRemove.cantidad)
    setCantidadItemsInvocados((prevSelect) => prevSelect - 1)
    setItemList(itemList.filter((_, i) => i !== index))

    showSnackbar(`Ítem "${itemToRemove.descripcion}" eliminado`, "info")
  }

  // Función para eliminar todos los ítems
  const handleDeleteAllItems = () => {
    if (itemList.length === 0) {
      showSnackbar("No hay ítems para eliminar", "info")
      return
    }

    // Limpiar todos los estados relacionados con items
    setItemList([])
    setPrecioTotal(0)
    setCantidadItemsSelect(0)
    setCantidadItemsInvocados(0)

    // Limpiar descuentos generales
    setValorDescuento(0)
    setPorcentajeDescuento(0)

    showSnackbar("Todos los ítems han sido eliminados", "info")
  }

  // Función para abrir modal de medio de pago
  const handleAbrirModalMedioDePago = () => {
    setOpenModalMedioPago(true)
  }

  // Función para agregar nuevo medio de pago
  const handleAddNuevoMedioDePago = () => {
    if (modalMedioDePagoSeleccionado && modalMedioDePagoCantidad) {
      const yaExiste = medioDePagoList.some((mp) => mp.id === modalMedioDePagoSeleccionadoID)
      if (yaExiste) {
        showSnackbar("Este medio de pago ya ha sido seleccionado", "warning")
        return
      }

      const newMedioDePago = {
        id: modalMedioDePagoSeleccionadoID,
        descripcion: modalMedioDePagoSeleccionado,
        monto: Number(Number.parseFloat(modalMedioDePagoCantidad).toFixed(2)),
        tipoMedioDePago: modalTipoMedioDePagoSeleccionado.id,
      }

      const nuevaLista = [...medioDePagoList, newMedioDePago]
      setMedioDePagoList(nuevaLista)

      const updatedTotalMedioPagoList = nuevaLista.reduce((total, item) => total + item.monto, 0)
      console.log("updatedTotalMedioPagoList ", updatedTotalMedioPagoList)
      console.log("precioTotal ", precioTotal)
      setCantidaMedioDePagoList(updatedTotalMedioPagoList)

      setModalMedioDePagoSeleccionado("")
      setModalMedioDePagoSeleccionadoID(null)
      setModalMedioDePagoCantidad("")

      showSnackbar("Medio de pago agregado", "success")
    } else {
      showSnackbar("Debe seleccionar un medio de pago y especificar un monto", "warning")
    }
  }

  // Función para eliminar medio de pago
  const handleDeleteMedioDePago = (index, medioDePagoMonto) => {
    setCantidaMedioDePagoList((prevTotal) => prevTotal - medioDePagoMonto)
    setMedioDePagoList(medioDePagoList.filter((_, i) => i !== index))

    showSnackbar("Medio de pago eliminado", "info")
  }

  // Función para cerrar modal de medio de pago
  const handleCloseModalMedioPago = () => {
    if (Math.abs(cantidaMedioDePagoList - precioTotal) > 0.01) {
      const confirmReset = window.confirm(
        "El total de medios de pago no es igual al precio de venta. ¿Desea reiniciar la selección?",
      )
      if (confirmReset) {
        setMedioDePagoList([])
        setCantidaMedioDePagoList(0)
      } else {
        return
      }
    }
    setOpenModalMedioPago(false)
  }

  // Función para verificar cliente
  const controlarDniIngresadoSiExiste = () => {
    if (dniCliente) {
      setEntidadExistente(dniCliente)
      setAbrirModalEntidadCliente(true)
    } else {
      setEntidadExistente(null)
      setAbrirModalEntidadCliente(true)
    }
  }

  // Función para abrir modal de creación de cliente
  const handleAbrirModalCrearCliente = () => {
    setAbrirModalEntidadCliente(false)
    setAbrirModalCrearCliente(true)
    setIsEditing(false)
  }

  // Función para cerrar modal de creación de cliente
  const handleCerrarModalCrearCliente = () => {
    setAbrirModalCrearCliente(false)
  }

  // Función para manejar cuando se crea un nuevo cliente
  const handleClienteCreado = (clienteData) => {
    // Autocompletar el campo cliente con el nuevo cliente creado
    setDniCliente(clienteData)
    
    // Actualizar la lista de entidades para que aparezca el nuevo cliente
    getEntidadesClientesTresLetras("", 1)
    
    console.log("Cliente creado y autocompletado:", clienteData)
  }

  // Función para abrir modal de modificación de cliente
  const abrirModificarCliente = (entidad) => {
    setEntidadAEditar(entidad)
    setIsEditing(true)
    setAbrirModalCrearCliente(true)
  }

  // Función para manejar alerta de creación/actualización de entidad
  const handleAlerta = (exito) => {
    if (exito) {
      showSnackbar("Entidad creada/actualizada correctamente", "success")
    } else {
      showSnackbar("Error al crear/actualizar la entidad", "error")
    }
  }

  // Función para cerrar modal de factura
  const handleCloseModal = () => {
    setOpenModal(false)
    // ✅ Si estamos en modo edición (hay id), navegar a la vista de ventas
    if (id) {
      navigate("tablaVentas")
    }
  }

  // Función para crear venta sin cliente
  const crearVentaIgualmente = () => {
    handleTransaccionSubmit(null, false)
  }

  // Función para manejar teclas en campos de búsqueda
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (document.activeElement === buttonRef.current) {
        autocompleteRef.current.focus()
      } else {
        buttonRef.current.focus()
      }
    }
  }

  // Función para manejar teclas y enviar formulario
  const handleKeyDownAndSubmit = (event) => {
    handleKeyDown(event)
    if (event.key === "Enter") {
      handleAddItem()
    }
  }

  // Función para generar etiqueta de ítem
  const generateItemLabel = (item) => {
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)

    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .join(" ")

    return `${item.codigo} - ${item.descripcion} ${attributeValues} ${item.codigoScanner}`
  }

  // Función para manejar cambio de ítem
  const handleItemChange = (event, newValue) => {
    console.log("🔍 Item seleccionado del Autocomplete:", newValue);
    console.log("🔍 ¿Tiene usaGestionLotes?", newValue?.usaGestionLotes);
    setSelectedItem(newValue)
    if (newValue) {
      const defaultValues = itemAtributoNoEliminados.map((attr, index) => ({
        value: newValue[`itemDatoAtributo${index + 1}`] || "",
      }))
      setAttributeValues(defaultValues)
      setCantidadTransaccionItem(1) // Resetea la cantidad al seleccionar un nuevo ítem
      // Solo hacer focus si el ref existe (en desktop)
      if (buttonRef.current) {
        buttonRef.current.focus()
      }
    }
  }

  // Función para mostrar snackbar
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }

  // Función para cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    })
  }

  // Función para enviar transacción
  const handleTransaccionSubmit = async (e, leerCliente, descripcionChequeModal) => {
    //se le agrega descripcion cheque ya que lamentablemente el estado de useState cambia mas lento que la ejecucion de la funcion
    // console.log("se esta ejecutando handleTransaccionSubmit")
    setLoading(true)
    console.log("se esta ejecutando handleTransaccionSubmit")

    try {
      if (leerCliente === true) {
        if (dniCliente) {
          setEntidadFactura(dniCliente)
        } else {
          setEntidadExistente(null)
          setAbrirModalEntidadCliente(true)
          setLoading(false)
          return
        }
      } else {
        setDniCliente(null)
      }
      console.log("dniCliente ", dniCliente)

      let condicionIva = 5

  // 2) Validaciones específicas por tipo de factura
  if (checkboxFacturaNegro == false) {

    const condicionIvaObjeto = await getCondicionIvaByIdEntidad(dniCliente.id)
    condicionIva = condicionIvaObjeto.idCondicionIva
    console.log("condicionIva ", condicionIva)

      // Normalizar idTipoFactura para comparaciones consistentes
    const tipoFacturaId = idTipoFactura?.id || idTipoFactura;

    // Factura A
    if (tipoFacturaId === 1) {
      if (condicionIva !== 1) {
        showSnackbar("Factura A solo corresponde a clientes Responsables Inscriptos", "error");
        setLoading(false);
        return;
      }
    }

    // Factura B
    if (tipoFacturaId === 2) {
      if (condicionIva === 1) {
        showSnackbar("A un Responsable Inscripto corresponde Factura A, no Factura B", "error");
        setLoading(false);
        return;
      }
      // Validar que corresponde a Monotributo (6), Exento (3) o Consumidor Final (5)
      if (![6, 3, 5].includes(condicionIva)) {
        showSnackbar("Factura B corresponde a Monotributo, Exento o Consumidor Final", "error");
        setLoading(false);
        return;
      }
    }

    // Factura C (solo por seguridad, emisor RI no debería poder elegirla)
    if (tipoFacturaId === 3) {
      showSnackbar("Siendo emisor Responsable Inscripto no podés emitir Factura C", "error");
      setLoading(false);
      return;
    }
  }

      setAbrirModalEntidadCliente(false)

      let precioTotalModificado = 0
      // Declarar array para precios actualizados (usado en ambos modos: edición y creación)
      const preciosActualizados = []

      console.log("medioDePagoList ", medioDePagoList)
      console.log("medioDePagoList.length ", medioDePagoList.length)

      if (
        (!selectedMedioDePago || !selectedTipoMedioDePago) &&
        medioDePagoList.length === 0
      ) {
        showSnackbar("Falta agregar un medio de pago", "error")
        setLoading(false)
        return
      }

      if (checkboxFacturaNegro == false) {
        const tipoFacturaId = idTipoFactura?.id || idTipoFactura;
        if (!tipoFacturaId) {
          showSnackbar("Falta seleccionar un tipo de factura", "error")
          setLoading(false)
          return
        }
      }

      // === 3) Apertura del modal de efectivo SÓLO si es efectivo y NO venimos del modal ===
      if (selectedTipoMedioDePago?.id === 1 && !leerCliente) {
        setLoading(false)
        setOpenModalCashPayment(true)
        return
      }

      // Normalizar idTipoFactura para comparaciones consistentes
      const tipoFacturaId = idTipoFactura?.id || idTipoFactura;
      
      // Si es Factura A y aún no tengo CUIT
      // 2) Si es Factura A y NO tengo CUIT aún, muestro el modal de CUIT
      if (tipoFacturaId === 1) {
        if (dniCliente.dniCuitCuil.length != 11) {
          showSnackbar("La entidad no posee CUIT", "error")
          return
        }
      } else if (tipoFacturaId === 2) {
        // Para Factura B: debe ser Monotributo (6), Exento (3) o Consumidor Final (5)
        if (![6, 3, 5].includes(condicionIva)) {
          showSnackbar("Factura B corresponde a Monotributo, Exento o Consumidor Final", "error")
          return
        }
      }

      // Si es efectivo (id = 1) y aún no abrimos el modal, lo mostramos y salimos
      if (selectedTipoMedioDePago?.id === 1 && !hasOpenedCashModal) {
        setLoading(false)
        setHasOpenedCashModal(true) // marcamos que ya lo mostramos
        setOpenModalCashPayment(true)
        return
      }

      if (selectedTipoMedioDePago?.id === 8 && !hasOpenedChequeModal) {
        //aca debemos respetar que siempre cheque debe ser id 8
        setLoading(false)
        setHasOpenedChequeModal(true) // marcamos que ya lo mostramos
        setOpenChequeDescripcion(true)
        return
      }

      // Validar precios antes de procesar
      const itemsConPreciosInvalidos = itemList.filter((it) => it.cantidad && (isNaN(it.precio) || it.precio <= 0))

      console.log("itemsConPreciosInvalidos ", itemsConPreciosInvalidos)

      if (itemsConPreciosInvalidos.length > 0) {
        const itemsInvalidos = itemsConPreciosInvalidos.map((it) => it.descripcion).join(", ")
        showSnackbar(`Los siguientes items tienen precios inválidos: ${itemsInvalidos}`, "error")
        setLoading(false)
        return
      }

      const descripcionTransaccionReemplazo = descripcionChequeModal ? descripcionChequeModal : descripcionTransaccion // Leemos si hay cheque, en caso de que haya se le pone la descripcion del cheque a la transaccion, sino la default que seria como vacio, OSEA descripcionTransaccion.
      console.log("descripcionChequeModal", descripcionChequeModal)
      console.log("descripcionTransaccionReemplazo", descripcionTransaccionReemplazo)
      precioTotalModificado = await precioTotal

      console.log("Precio total modificado:", precioTotalModificado)

      let transaccionResponse
      let idTransaccion
      let pagosCambiaron = true // Por defecto, en modo creación siempre se crean pagos
      let fechaOriginalTransaccion = null // Para mantener la fecha original en ediciones

      // 🔄 Detectar si estamos editando una venta existente o creando una nueva
      if (id) {
        console.log("🔄 Modo EDICIÓN: Actualizando venta existente con ID:", id)
        
        // 📋 Obtener transacción existente para manejar pagos y cuenta corriente
        const transaccionExistente = await getTransaccionFilter(id)
        console.log("📦 Transacción existente cargada:", transaccionExistente)
        
        // 📅 Guardar la fecha original de la transacción para los pagos
        fechaOriginalTransaccion = transaccionExistente?.fechaHoraCreacion || transaccionExistente?.createdAt
        console.log("📅 Fecha original de la transacción:", fechaOriginalTransaccion)
        
        // 📋 Función auxiliar para comparar pagos y detectar cambios
        const compararPagos = (pagosOriginales, pagosActuales, tipoActual, montoTotal) => {
          // Si no hay pagos originales, hay cambios
          if (!pagosOriginales || pagosOriginales.length === 0) {
            return true
          }

          // Comparar tipo de medio de pago
          const tipoOriginal = pagosOriginales.length > 1 ? 7 : pagosOriginales[0]?.pago?.medioDePago?.idTipoMedioDePago
          
          if (tipoOriginal !== tipoActual?.id) {
            console.log("🔄 Tipo de medio de pago cambió:", tipoOriginal, "→", tipoActual?.id)
            return true
          }

          // Si es múltiple (id=7)
          if (tipoActual?.id === 7) {
            // ⚠️ VALIDACIÓN: Si medioDePagoList está vacío, asumir sin cambios
            if (!pagosActuales || pagosActuales.length === 0) {
              console.log("⚠️ Lista de pagos múltiples vacía, asumiendo sin cambios")
              return false  // Si está vacía, asumir que no cambió
            }
            
            // Comparar cantidad de pagos
            if (pagosOriginales.length !== pagosActuales.length) {
              console.log("🔄 Cantidad de medios de pago múltiples cambió")
              return true
            }

            // Comparar cada pago
            for (let i = 0; i < pagosOriginales.length; i++) {
              const pagoOrig = pagosOriginales[i]
              const pagoActual = pagosActuales.find(p => p.id === pagoOrig.pago?.medioDePago?.id)
              
              if (!pagoActual || Math.abs(pagoActual.monto - pagoOrig.pago?.montoTotal) > 0.01) {
                console.log("🔄 Medio de pago múltiple cambió o monto diferente")
                return true
              }
            }
            
            return false // No hubo cambios
          } else {
            // Pago individual: comparar medio de pago y monto
            const medioDePagoOrig = pagosOriginales[0]?.pago?.medioDePago?.id
            const medioDePagoActual = pagosActuales?.id
            const montoOrig = pagosOriginales[0]?.pago?.montoTotal

            // ⚠️ VALIDACIÓN: Si selectedMedioDePago está vacío o es null, asumir sin cambios
            if (!pagosActuales || (Array.isArray(pagosActuales) && pagosActuales.length === 0)) {
              console.log("⚠️ selectedMedioDePago vacío/null, asumiendo sin cambios")
              return false
            }

            if (medioDePagoOrig !== medioDePagoActual) {
              console.log("🔄 Medio de pago individual cambió:", medioDePagoOrig, "→", medioDePagoActual)
              return true
            }

            if (Math.abs(montoOrig - montoTotal) > 0.01) {
              console.log("🔄 Monto de pago cambió:", montoOrig, "→", montoTotal)
              return true
            }

            return false // No hubo cambios
          }
        }
        
        // 🔄 PASO 1: RE-STOCK - Devolver stock de items originales
        console.log("📦 Iniciando re-stock de items originales...")
        try {
          // Obtener TODOS los items originales (incluyendo eliminados para obtener info completa)
          const itemsOriginales = await getTransaccionItemsByTransactionId(id)
          console.log(`📦 Items originales encontrados: ${itemsOriginales.length}`)
          
          // Usar la ubicación de la transacción original como fallback
          const ubicacionOriginal = transaccionExistente.idUbicacion || selectedUbicacion?.id || 1
          
          // Devolver stock de items que NO estaban eliminados
          for (const itemOrig of itemsOriginales) {
            if (!itemOrig.eliminado) {
              // Usar idUbicacion del item, o la ubicación de la transacción como fallback
              const ubicacionParaRevertir = itemOrig.idUbicacion || ubicacionOriginal
              console.log(`🔄 Devolviendo stock de item ${itemOrig.idItem}: +${itemOrig.cantidad} unidades en ubicación ${ubicacionParaRevertir}`)
              
              // El parámetro "compra" SUMA al stock
              await updateItemUbicacion(itemOrig.idItem, ubicacionParaRevertir, itemOrig.cantidad, "compra")
              console.log(`✅ Stock devuelto para item ${itemOrig.idItem}`)
            }
          }
          console.log("✅ Re-stock de items completado")
        } catch (error) {
          console.error("❌ Error al revertir stock de items:", error)
        }
        
        // 🔄 PASO 2: RE-STOCK DE LOTES - Devolver stock de lotes originales
        if (gestionDeLotesActiva) {
          console.log("📦 Iniciando re-stock de lotes originales...")
          try {
            const resultado = await revertirLotesDeTransaccion(id)
            console.log(`✅ Re-stock de lotes completado: ${resultado.lotesRevertidos} lotes revertidos`)
          } catch (error) {
            console.error("❌ Error al revertir stock de lotes:", error)
          }
        }
        
        // Usar valores de la transacción original como fallback si no se seleccionaron nuevos
        const idUbicacionFinal = selectedUbicacion?.id || transaccionExistente.idUbicacion || 1
        const idNegocioFinal = selectedNegocio?.id || transaccionExistente.idNegocio || 1
        const idUsuarioFinal = usuarioSeleccionado?.id || transaccionExistente.idUsuario || Number.parseInt(Cookies.get("idUsuario"), 10)
        
        console.log("📋 Datos finales para actualizar transacción:", {
          idUbicacion: idUbicacionFinal,
          idNegocio: idNegocioFinal,
          idUsuario: idUsuarioFinal
        })
        
        // Actualizar la transacción existente (ORDEN CORRECTO DE PARÁMETROS)
        transaccionResponse = await putTransaccion(
          id,                              // idTransaccion
          precioTotalModificado,           // montoTotal
          1,                               // idTipoTransaccion (1 = Venta)
          dniCliente.id,                   // idEntidad
          null,                            // idCategorizacion (ventas no usan categorización)
          descripcionTransaccionReemplazo, // descripcion
          idUsuarioFinal,                  // idUsuario
          idUbicacionFinal,                // idUbicacion
          idNegocioFinal,                  // idNegocio
          tempValorDescuento,              // montoDescuento
          tempPorcentajeDescuento,         // porcentajeDescuento
          null,                            // transaccionAsociada (no aplica para ventas)
          fechaOriginalTransaccion,        // ✅ fecha original (preservar la fecha de creación)
        )
        
        idTransaccion = id // Usar el ID existente

        // ✅ Calcular el total base para distribuir descuentos de transacción (necesario para impuestos)
        const totalBaseTransaccion = itemList.reduce((total, item) => {
          return total + getItemLineTotal(item)
        }, 0)

        // ✅ Calcular descuentos de transacción
        const valorDescTransaccion = Math.floor((Number.parseFloat(tempValorDescuento) || 0) * 100) / 100
        const porcentajeDescTransaccion = Math.floor((Number.parseFloat(tempPorcentajeDescuento) || 0) * 100) / 100

        // 🔄 LÓGICA INTELIGENTE: Comparar items existentes vs itemList para decidir qué hacer
        try {
          // 1. Obtener items existentes de BD (TODOS, incluyendo eliminados)
          const itemsExistentes = await getTransaccionItemsByTransactionId(id)
          const itemsExistentesMap = new Map(
            itemsExistentes.map(item => [item.idItem, item]) // ✅ Incluir TODOS (eliminados y no eliminados)
          )
          console.log(`📦 Items existentes en BD (total): ${itemsExistentesMap.size}`)

          // 2. Crear mapa de items en itemList
          const itemsListMap = new Map(
            itemList.map(item => [item.idItem, item])
          )
          console.log(`📋 Items en itemList: ${itemsListMap.size}`)

          // 3. Procesar cada item existente (activos y eliminados)
          for (const [idItem, itemExistente] of itemsExistentesMap) {
            const itemNuevo = itemsListMap.get(idItem)
            
            if (!itemNuevo) {
              // Item existe en BD pero NO en itemList → ELIMINAR (solo si no está ya eliminado)
              if (!itemExistente.eliminado) {
                console.log(`🗑️ Eliminando item ${idItem} (no está en itemList)`)
                await deleteTransaccionItem(id, idItem)
              } else {
                console.log(`✅ Item ${idItem} ya está eliminado, no hacer nada`)
              }
            } else {
              // Item existe en BD Y en itemList → REAPLICAR (siempre) y reactivar si estaba eliminado
              const porcentajeDescuentoNuevo = itemNuevo.porcentajeDescuento || 0
              const porcentajeInteresNuevo = itemNuevo.porcentajeInteres || 0
              const esItemConLotes = itemNuevo.usaGestionLotes && gestionDeLotesActiva

              // Actualizar precio en lista si aplica
              if (precioEnVentaEditable && itemExistente.precio !== itemNuevo.precio) {
                try {
                  await updateItemPrecioListaId(itemNuevo.idItem, dniCliente.id, itemNuevo.idUbicacion, itemNuevo.precio, "valor")
                  preciosActualizados.push(itemNuevo.descripcion || `Item ${idItem}`)
                } catch (error) {
                  console.error(`Error actualizando precio para ${itemNuevo.descripcion}:`, error)
                }
              }

              if (esItemConLotes) {
                // Para lotes: limpiar estado anterior y recrear con los lotes seleccionados
                await deleteTransaccionItem(id, idItem)

                const lotesParaEnviar = lotesDelCarrito[itemNuevo.idItem]
                  ? lotesDelCarrito[itemNuevo.idItem].map(lote => ({
                      idLote: lote.idLote,
                      cantidad: lote.cantidad
                    }))
                  : []

                await postTransaccionItem(
                  idTransaccion,
                  itemNuevo.idItem,
                  itemNuevo.cantidad,
                  itemNuevo.precio,
                  porcentajeDescuentoNuevo,
                  lotesParaEnviar,
                  itemNuevo.idUbicacion,
                  porcentajeInteresNuevo,
                )

                // Impuestos para item con lotes
                if (checkboxFacturaNegro === false && itemNuevo.impuestoData) {
                  try {
                    const porcentajeImpuesto = itemNuevo.impuestoData.porcentaje || 21;
                    const precioConAjustesItem = Math.floor((
                      itemNuevo.precio *
                      (1 - (itemNuevo.porcentajeDescuento || 0) / 100) *
                      (1 + (itemNuevo.porcentajeInteres || 0) / 100)
                    ) * 100) / 100
                    const totalItem = precioConAjustesItem * itemNuevo.cantidad
                    const proporcionItem = totalBaseTransaccion > 0 ? totalItem / totalBaseTransaccion : 0
                    const descuentoFijoItem = Math.floor((valorDescTransaccion * proporcionItem) * 100) / 100
                    const descuentoPorcentualItem = Math.floor((totalItem * (porcentajeDescTransaccion / 100)) * 100) / 100
                    let precioFinalItem = Math.floor((precioConAjustesItem - (descuentoFijoItem / itemNuevo.cantidad) - (descuentoPorcentualItem / itemNuevo.cantidad)) * 100) / 100
                    if (precioFinalItem < 0) precioFinalItem = 0
                    const precioItem = precioFinalItem * itemNuevo.cantidad;
                    const montoImpuestoItem = precioItem - (precioItem / (1 + (porcentajeImpuesto / 100)));

                    await updateTransaccionImpuestoItem(id, idItem, {
                      idImpuesto: itemNuevo.impuestoData.idIva,
                      porcentaje: porcentajeImpuesto,
                      montoTotal: montoImpuestoItem
                    });
                  } catch (error) {
                    console.error(`Error actualizando impuesto del item ${itemNuevo.descripcion || idItem}:`, error);
                  }
                }
              } else {
                await updateTransaccionItem(id, idItem, {
                  cantidad: itemNuevo.cantidad,
                  precio: itemNuevo.precio,
                  porcentajeDescuento: porcentajeDescuentoNuevo,
                  porcentajeInteres: porcentajeInteresNuevo,
                })

                // 🔄 Impuestos para item sin lotes
                if (checkboxFacturaNegro === false && itemNuevo.impuestoData) {
                  try {
                    const porcentajeImpuesto = itemNuevo.impuestoData.porcentaje || 21;
                    const precioConAjustesItem = Math.floor((
                      itemNuevo.precio *
                      (1 - (itemNuevo.porcentajeDescuento || 0) / 100) *
                      (1 + (itemNuevo.porcentajeInteres || 0) / 100)
                    ) * 100) / 100
                    const totalItem = precioConAjustesItem * itemNuevo.cantidad
                    const proporcionItem = totalBaseTransaccion > 0 ? totalItem / totalBaseTransaccion : 0
                    const descuentoFijoItem = Math.floor((valorDescTransaccion * proporcionItem) * 100) / 100
                    const descuentoPorcentualItem = Math.floor((totalItem * (porcentajeDescTransaccion / 100)) * 100) / 100
                    let precioFinalItem = Math.floor((precioConAjustesItem - (descuentoFijoItem / itemNuevo.cantidad) - (descuentoPorcentualItem / itemNuevo.cantidad)) * 100) / 100
                    if (precioFinalItem < 0) {
                      precioFinalItem = 0
                    }
                    const precioItem = precioFinalItem * itemNuevo.cantidad;
                    const montoImpuestoItem = precioItem - (precioItem / (1 + (porcentajeImpuesto / 100)));

                    await updateTransaccionImpuestoItem(id, idItem, {
                      idImpuesto: itemNuevo.impuestoData.idIva,
                      porcentaje: porcentajeImpuesto,
                      montoTotal: montoImpuestoItem
                    });
                  } catch (error) {
                    console.error(`Error actualizando impuesto del item ${itemNuevo.descripcion || idItem}:`, error);
                  }
                }

                // Descontar stock siempre (sin lotes)
                if (!itemNuevo.usaGestionLotes || !gestionDeLotesActiva) {
                  const ubicacionItem = await getItemUbicacionFilterByUbicacion(itemNuevo.idItem, itemNuevo.idUbicacion)
                  if (ubicacionItem) {
                    const nuevoStock = itemNuevo.cantidad
                    if (nuevoStock >= 0) {
                      await updateItemUbicacion(itemNuevo.idItem, ubicacionItem[0].idUbicacion, nuevoStock, "venta")
                    }
                  }
                }
              }
            }
          }

          // 4. Procesar items nuevos (están en itemList pero NO en BD - ni eliminados ni activos)
          for (const [idItem, itemNuevo] of itemsListMap) {
            if (!itemsExistentesMap.has(idItem)) {
              // Item NO existe en BD pero está en itemList → CREAR
              console.log(`✨ Creando nuevo item ${idItem}`)
              
              // Actualizar precio en lista si aplica
              if (precioEnVentaEditable) {
                try {
                  await updateItemPrecioListaId(itemNuevo.idItem, dniCliente.id, itemNuevo.idUbicacion, itemNuevo.precio, "valor")
                  preciosActualizados.push(itemNuevo.descripcion)
                } catch (error) {
                  console.error(`Error actualizando precio para ${itemNuevo.descripcion}:`, error)
                }
              }

              // Preparar lotes si el item usa gestión de lotes
              const lotesParaEnviar = itemNuevo.usaGestionLotes && lotesDelCarrito[itemNuevo.idItem] 
                ? lotesDelCarrito[itemNuevo.idItem].map(lote => ({
                    idLote: lote.idLote,
                    cantidad: lote.cantidad
                  }))
                : undefined;
              
              await postTransaccionItem(
                idTransaccion,
                itemNuevo.idItem,
                itemNuevo.cantidad,
                itemNuevo.precio,
                itemNuevo.porcentajeDescuento || 0,
                lotesParaEnviar,
                itemNuevo.idUbicacion,
                itemNuevo.porcentajeInteres || 0,
              )
              
              // Crear TransaccionImpuestoItem si aplica
              if (checkboxFacturaNegro === false && itemNuevo.impuestoData) {
                try {
                  const porcentajeImpuesto = itemNuevo.impuestoData.porcentaje || 21;

                  // Calcular el precio unitario después de aplicar descuento/interés del item
                  const precioConAjustesItem = Math.floor((
                    itemNuevo.precio *
                    (1 - (itemNuevo.porcentajeDescuento || 0) / 100) *
                    (1 + (itemNuevo.porcentajeInteres || 0) / 100)
                  ) * 100) / 100

                  // Calcular la proporción de descuento de transacción que le corresponde a este item
                  const totalItem = precioConAjustesItem * itemNuevo.cantidad
                  const proporcionItem = totalBaseTransaccion > 0 ? totalItem / totalBaseTransaccion : 0

                  // Calcular descuentos de transacción aplicados a este item
                  const descuentoFijoItem = Math.floor((valorDescTransaccion * proporcionItem) * 100) / 100
                  const descuentoPorcentualItem = Math.floor((totalItem * (porcentajeDescTransaccion / 100)) * 100) / 100

                  // Precio final del item considerando TODOS los descuentos
                  let precioFinalItem = Math.floor((precioConAjustesItem - (descuentoFijoItem / itemNuevo.cantidad) - (descuentoPorcentualItem / itemNuevo.cantidad)) * 100) / 100

                  // Asegurar que el precio final no sea negativo
                  if (precioFinalItem < 0) {
                    precioFinalItem = 0
                  }

                  const precioItem = precioFinalItem * itemNuevo.cantidad;

                  // Calcular monto del impuesto para este item específico CON TODOS LOS DESCUENTOS APLICADOS
                  const montoImpuestoItem = precioItem - (precioItem / (1 + (porcentajeImpuesto / 100)));

                  await postTransaccionImpuestoItem(
                    idTransaccion,
                    itemNuevo.idItem,
                    itemNuevo.impuestoData.idIva,
                    porcentajeImpuesto,
                    montoImpuestoItem
                  );

                  console.log(`Impuesto item guardado: ${itemNuevo.descripcion} - ${porcentajeImpuesto}% - $${montoImpuestoItem.toFixed(2)}`);
                } catch (error) {
                  console.error(`Error guardando impuesto del item ${itemNuevo.descripcion}:`, error);
                }
              }

              // Descontar stock si no usa gestión de lotes
              if (!itemNuevo.usaGestionLotes || !gestionDeLotesActiva) {
                const ubicacionItem = await getItemUbicacionFilterByUbicacion(itemNuevo.idItem, itemNuevo.idUbicacion)
                if (ubicacionItem) {
                  const nuevoStock = itemNuevo.cantidad
                  if (nuevoStock >= 0) {
                    await updateItemUbicacion(itemNuevo.idItem, ubicacionItem[0].idUbicacion, nuevoStock, "venta")
                  }
                }
              }
            }
          }

          console.log("✅ Procesamiento inteligente de items completado")
        } catch (error) {
          console.error("Error al procesar items:", error)
        }
        
        // 🔄 PASO 5: Evaluar si los medios de pago cambiaron antes de eliminar/recrear
        const pagosOriginales = transaccionExistente?.transaccionPago || []
        pagosCambiaron = compararPagos(
          pagosOriginales,
          selectedTipoMedioDePago.id === 7 ? medioDePagoList : selectedMedioDePago,
          selectedTipoMedioDePago,
          precioTotalModificado
        )

        if (pagosCambiaron) {
          console.log("💳 Los medios de pago CAMBIARON - Procesando reversión y eliminación")
          
          // 🔄 Revertir cuenta corriente de los pagos antiguos (si afectaban CC)
          if (transaccionExistente?.transaccionPago && transaccionExistente.transaccionPago.length > 0) {
            console.log("🔄 Iniciando reversión de cuenta corriente...")
            console.log("📋 Pagos encontrados:", transaccionExistente.transaccionPago.length)
            
            // Determinar con qué operación se impactó originalmente la cuenta corriente
            const usoOperacionCajaOriginal = transaccionExistente?.operacionParaCuentaCorriente === "operacionCaja"
            const tipoTransaccionData = transaccionExistente?.tipoTransaccion
            const operacionCC = tipoTransaccionData?.operacionCuentaCorriente
            const operacionCaja = tipoTransaccionData?.operacionCaja
            
            let montoTotalARevertir = 0
            
            // Solo considerar pagos cuyo tipo de medio afecta CC
            for (const tp of transaccionExistente.transaccionPago) {
              const pagoData = tp.pago
              const medioDePagoData = pagoData?.medioDePago
              const tipoMedioDePagoData = medioDePagoData?.tipoMedioDePago
              const tipoMedioId = tipoMedioDePagoData?.id || medioDePagoData?.idTipoMedioDePago
              
              let afectaCuentaCorrienteMedio = tipoMedioDePagoData?.afectaCuentaCorriente
              if (afectaCuentaCorrienteMedio === undefined && tipoMedioId) {
                const tipoMedioGlobal = tipoMedioDePago.find((tipo) => tipo.id === tipoMedioId)
                afectaCuentaCorrienteMedio = tipoMedioGlobal?.afectaCuentaCorriente
              }
              
              console.log("📋 Analizando pago:", {
                idPago: pagoData?.id,
                montoTotal: pagoData?.montoTotal,
                medioDePago: medioDePagoData?.descripcion,
                tipoMedioDePago: tipoMedioDePagoData?.descripcion || tipoMedioDePago.find((tipo) => tipo.id === tipoMedioId)?.descripcion,
                afectaCuentaCorriente: afectaCuentaCorrienteMedio,
              })
              
              if (afectaCuentaCorrienteMedio === true) {
                montoTotalARevertir += Math.abs(pagoData?.montoTotal || 0)
              }
            }
            
            if (montoTotalARevertir === 0 && transaccionExistente?.afectaCuentaCorriente) {
              montoTotalARevertir = Math.abs(transaccionExistente?.montoTotal || 0)
              console.log("🔄 La transacción completa afecta cuenta corriente. Monto a revertir:", montoTotalARevertir)
            }
            
            console.log(`🔄 Monto total de pagos que afectaron cuenta corriente: ${montoTotalARevertir}`)
            
            if (montoTotalARevertir > 0) {
              const usarOperacionCajaParaReversion = !usoOperacionCajaOriginal
              const operacionQueSeAplicará = usarOperacionCajaParaReversion ? operacionCaja : operacionCC
              
              if (!operacionQueSeAplicará) {
                console.warn("⚠️ No se pudo determinar la operación inversa para la transacción. No se revertirá cuenta corriente.")
              } else {
                await updateCuentaCorriente(
                  dniCliente.id,
                  montoTotalARevertir,
                  transaccionExistente?.idTipoTransaccion || 1,
                  usarOperacionCajaParaReversion
                )
                console.log(`✅ Cuenta corriente revertida aplicando ${usarOperacionCajaParaReversion ? "operacionCaja" : "operacionCuentaCorriente"} por $${montoTotalARevertir}`)
              }
            } else {
              console.log("ℹ️ No hay montos que revertir en cuenta corriente para esta transacción")
            }
          } else {
            console.log("⚠️ No hay pagos existentes para revisar")
          }
          
          // 🗑️ Eliminar lógicamente los pagos existentes (TransaccionPago y Pago)
          try {
            const result = await deleteTransaccionPago(id)
            if (result.affectedTransaccionPagos > 0) {
              console.log(`✅ ${result.affectedTransaccionPagos} TransaccionPago y ${result.affectedPagos || 0} Pago marcados como eliminados`)
            } else {
              console.log("⚠️ No había pagos existentes para eliminar")
            }
          } catch (error) {
            console.error("Error al eliminar pagos existentes:", error)
          }
        } else {
          console.log("✅ Los medios de pago NO cambiaron - Manteniendo pagos existentes sin modificar")
        }

      } else {
        console.log("✨ Modo CREACIÓN: Generando nueva venta")
        
        // Crear una nueva transacción
        transaccionResponse = await postTransaccion(
        precioTotalModificado,
        1,
        dniCliente.id,
        null,
        descripcionTransaccionReemplazo,
        usuarioSeleccionado.id,
        selectedUbicacion.id,
        selectedNegocio.id,
        tempValorDescuento,
        tempPorcentajeDescuento,
      )

        idTransaccion = await transaccionResponse.data.id
      }

      if (checkboxFacturaNegro == false) {
        const impuestosIncluidos = impuestos.every((impuesto) => impuesto.incluidoEnPrecio)

        let montoBase = precioBaseFinalConDescuento

        if (impuestosIncluidos) {
          montoBase =
            precioTotal /
            (1 + impuestos.filter((imp) => !imp.eliminado).reduce((sum, imp) => sum + imp.porcentaje / 100, 0))
        }

        for (const imp of impuestos.filter((imp) => !imp.eliminado)) {
          const montoImpuesto = montoBase * (imp.porcentaje / 100)

          await postTransaccionImpuesto(idTransaccion, imp.id, imp.porcentaje, montoImpuesto)
        }
      }

      // 💳 CREACIÓN DE PAGOS: Solo si es modo creación O si los pagos cambiaron en edición
      if (pagosCambiaron) {
        console.log("💳 Creando nuevos pagos...")
        
        if (selectedTipoMedioDePago.id == 7) {
          for (const it of medioDePagoList) {
            const transaccionCrearPago = await postCrearPago(1, 1, it.id, it.monto, fechaOriginalTransaccion)

            // Verificar si el tipo de medio de pago afecta cuenta corriente usando el flag
            const tipoMedio = tipoMedioDePago.find(t => t.id === it.tipoMedioDePago)
            if (tipoMedio?.afectaCuentaCorriente === true) {
              const cuentas = await getCuentaCorrienteByIdEntidad(dniCliente.id)
              const cuentaCorrienteConId = cuentas.find((ct) => ct.idEntidad === dniCliente.id)
              if (cuentaCorrienteConId) {
                await updateCuentaCorriente(dniCliente.id, it.monto, 1)
              } else {
                await postCuentaCorriente(dniCliente.id, it.monto, 1)
              }
            }

            const idTransaccionCrearPago = transaccionCrearPago.data.id
            await postTransaccionPago(idTransaccion, idTransaccionCrearPago, fechaOriginalTransaccion)
          }
        } else {
          const transaccionCrearPago = await postCrearPago(1, 1, selectedMedioDePago.id, precioTotalModificado, fechaOriginalTransaccion)

          // Verificar si el tipo de medio de pago afecta cuenta corriente usando el flag
          if (selectedTipoMedioDePago?.afectaCuentaCorriente === true) {
            const cuentas = await getCuentaCorrienteByIdEntidad(dniCliente.id)
            const cuentaCorrienteConId = cuentas.find((ct) => ct.idEntidad === dniCliente.id)
            if (cuentaCorrienteConId) {
              await updateCuentaCorriente(dniCliente.id, precioTotalModificado, 1)
            } else {
              await postCuentaCorriente(dniCliente.id, precioTotalModificado, 1)
            }
          }

          const idTransaccionCrearPago = transaccionCrearPago.data.id
          await postTransaccionPago(idTransaccion, idTransaccionCrearPago, fechaOriginalTransaccion)
        }
        
        console.log("✅ Nuevos pagos creados correctamente")
      }

      // ✅ Calcular el total base para distribuir descuentos de transacción
      const totalBaseTransaccion = itemList.reduce((total, item) => {
        return total + getItemLineTotal(item)
      }, 0)

      // ✅ Calcular descuentos de transacción
      const valorDescTransaccion = Math.floor((Number.parseFloat(tempValorDescuento) || 0) * 100) / 100
      const porcentajeDescTransaccion = Math.floor((Number.parseFloat(tempPorcentajeDescuento) || 0) * 100) / 100

      // 🔄 Procesar items: Solo en modo CREACIÓN (en modo edición ya se procesaron arriba)
      if (!id) {
        // Procesar items válidos (MODO CREACIÓN)
      for (const it of itemList) {
        if (it.cantidad) {
          if (precioEnVentaEditable) {
            try {
              await updateItemPrecioListaId(it.idItem, dniCliente.id, it.idUbicacion, it.precio, "valor")
              preciosActualizados.push(it.descripcion)
            } catch (error) {
              console.error(`Error actualizando precio para ${it.descripcion}:`, error)
            }
          }

          // 🆕 Preparar lotes si el item usa gestión de lotes
          const lotesParaEnviar = it.usaGestionLotes && lotesDelCarrito[it.idItem] 
            ? lotesDelCarrito[it.idItem].map(lote => ({
                idLote: lote.idLote,
                cantidad: lote.cantidad
              }))
            : undefined;

          await postTransaccionItem(
            idTransaccion, 
            it.idItem, 
            it.cantidad, 
            it.precio, 
            it.porcentajeDescuento,
            lotesParaEnviar, // 🆕 Enviar lotes al backend
            it.idUbicacion, // 🆕 Enviar ubicación
            it.porcentajeInteres || 0,
          )

          // ✅ POST TransaccionImpuestoItem para trazabilidad de impuestos por item
          if (checkboxFacturaNegro === false && it.impuestoData) {
            try {
              const porcentajeImpuesto = it.impuestoData.porcentaje || 21;

              // ✅ Calcular el precio unitario después de aplicar descuento/interés del item
              const precioConAjustesItem = Math.floor((
                it.precio *
                (1 - (it.porcentajeDescuento || 0) / 100) *
                (1 + (it.porcentajeInteres || 0) / 100)
              ) * 100) / 100

              // ✅ Calcular la proporción de descuento de transacción que le corresponde a este item
              const totalItem = precioConAjustesItem * it.cantidad
              const proporcionItem = totalBaseTransaccion > 0 ? totalItem / totalBaseTransaccion : 0

              // ✅ Calcular descuentos de transacción aplicados a este item
              const descuentoFijoItem = Math.floor((valorDescTransaccion * proporcionItem) * 100) / 100
              const descuentoPorcentualItem = Math.floor((totalItem * (porcentajeDescTransaccion / 100)) * 100) / 100

              // ✅ Precio final del item considerando TODOS los descuentos
              let precioFinalItem = Math.floor((precioConAjustesItem - (descuentoFijoItem / it.cantidad) - (descuentoPorcentualItem / it.cantidad)) * 100) / 100

              // ✅ Asegurar que el precio final no sea negativo
              if (precioFinalItem < 0) {
                precioFinalItem = 0
              }

              const precioItem = precioFinalItem * it.cantidad;

              // ✅ Calcular el precio sin IVA considerando TODOS los descuentos aplicados
              const precioSinIva = Math.floor((precioFinalItem / (1 + (porcentajeImpuesto / 100))) * 100) / 100

              // ✅ Calcular monto del impuesto para este item específico CON TODOS LOS DESCUENTOS APLICADOS
              const montoImpuestoItem = precioItem - (precioItem / (1 + (porcentajeImpuesto / 100)));

              await postTransaccionImpuestoItem(
                idTransaccion,
                it.idItem,
                it.impuestoData.idIva, // Default a IVA 21%
                porcentajeImpuesto,
                montoImpuestoItem
              );

              // ✅ Debug: Log de cálculos para verificar que los descuentos se aplican correctamente
              console.log(`Item ${it.idItem} - Cálculos de descuentos e IVA:`, {
                precioOriginal: it.precio,
                descuentoItem: it.porcentajeDescuento || 0,
                interesItem: it.porcentajeInteres || 0,
                precioConAjustesItem,
                totalItem,
                proporcionItem,
                descuentoFijoItem,
                descuentoPorcentualItem,
                precioFinalItem,
                precioSinIva,
                precioItem,
                montoImpuestoItem,
                porcentajeImpuesto
              })

              console.log(`Impuesto item guardado: ${it.descripcion} - ${porcentajeImpuesto}% - $${montoImpuestoItem.toFixed(2)}`);
            } catch (error) {
              console.error(`Error guardando impuesto del item ${it.descripcion}:`, error);
            }
          }

          // 🆕 NO descontar de ItemUbicacion si el item usa gestión de lotes
          // El backend ya descuenta de ambas tablas (ItemUbicacion y LoteItemUbicacion) cuando hay lotes
          if (!it.usaGestionLotes || !gestionDeLotesActiva) {
            const ubicacionItem = await getItemUbicacionFilterByUbicacion(it.idItem, it.idUbicacion)

            if (ubicacionItem) {
              const nuevoStock = it.cantidad

              if (nuevoStock < 0) {
                console.log(`Stock insuficiente para ${it.descripcion}`)
                continue
              }

              await updateItemUbicacion(it.idItem, ubicacionItem[0].idUbicacion, nuevoStock, "venta")
            }
          } else {
            console.log(`✅ Item "${it.descripcion}" usa lotes - el backend ya descontó de ambas tablas`)
          }
        }
      }
      } // Fin del if (!id) - Solo procesar items en modo creación

      let descripcion = ""

      if (dniCliente == null || dniCliente.id == null) {
        descripcion = "Cliente no encontrado"
      } else {
        descripcion = dniCliente.descripcion
      }

      let medioDePagoPDF = []

      if (selectedTipoMedioDePago.id != 7) {
        const newMedioDePago = {
          id: selectedMedioDePago.id,
          descripcion: selectedMedioDePago.descripcion,
          monto: precioTotalModificado,
          tipoMedioDePago: selectedMedioDePago.idTipoMedioDePago,
        }

        medioDePagoPDF = newMedioDePago
      } else {
        medioDePagoPDF = medioDePagoList
      }

      const fechaTransaccion = transaccionResponse?.data?.fecha || new Date().toISOString()

      const transaccionData = {
        id: idTransaccion,
        cliente: descripcion,
        pagos: medioDePagoPDF,
        montoDescuento: tempValorDescuento,
        porcentajeDescuento: tempPorcentajeDescuento,
        fecha: fechaTransaccion,
        fechaHoraCreacion: transaccionResponse?.data?.fechaHoraCreacion || fechaTransaccion,
      }

      //*********************************************************************************************************** */
      //***********************************************************************************************************
      // AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP 
      // AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP 
      // AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP 
      // AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP 
      // AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP 
      // AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP 
      // AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP 
      // AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP AFIP 
      // //Configuracion AFIP */
      if (checkboxFacturaNegro == false) {
        // 1) Fecha en formato YYYYMMDD
        const now = new Date()
        const dd = String(now.getDate()).padStart(2, "0")
        const mm = String(now.getMonth() + 1).padStart(2, "0")
        const yyyy = now.getFullYear()
        const fechaCbte = Number(`${yyyy}${mm}${dd}`)

        // ——————————————————————————————————————————
        // 2) DocTipo / DocNro
        let docTipo = 99 // Por defecto: Consumidor Final sin documento
        let docNro = 0

        // Normalizar idTipoFactura para comparaciones consistentes
        const tipoFacturaId = idTipoFactura?.id || idTipoFactura;
        
        if (tipoFacturaId === 1) {
          // Factura A: siempre requiere CUIT (ya validado antes)
          docTipo = 80 // CUIT
          docNro = dniCliente.dniCuitCuil
        } else if (tipoFacturaId === 2) {
          // Factura B: lógica mejorada según condición IVA
          if (condicionIva === 6) {
            // Monotributista: priorizar CUIT si tiene 11 dígitos, sino DNI
            if (dniCliente.dniCuitCuil && dniCliente.dniCuitCuil.length === 11) {
              docTipo = 80 // CUIT
              docNro = dniCliente.dniCuitCuil
            } else if (dniCliente.dniCuitCuil && dniCliente.dniCuitCuil.length >= 7) {
              docTipo = 96 // DNI
              docNro = dniCliente.dniCuitCuil
            }
            // Si no tiene documento válido, queda como Consumidor Final (99/0)
          } else if (condicionIva === 3) {
            // Exento: priorizar CUIT si tiene 11 dígitos, sino DNI
            if (dniCliente.dniCuitCuil && dniCliente.dniCuitCuil.length === 11) {
              docTipo = 80 // CUIT
              docNro = dniCliente.dniCuitCuil
            } else if (dniCliente.dniCuitCuil && dniCliente.dniCuitCuil.length >= 7) {
              docTipo = 96 // DNI
              docNro = dniCliente.dniCuitCuil
            }
            // Si no tiene documento válido, queda como CF (99/0)
          } else if (condicionIva === 5) {
            // Consumidor Final: puede tener DNI o ir sin documento
            if (dniCliente.id !== 1 && dniCliente.dniCuitCuil && dniCliente.dniCuitCuil.length >= 7) {
              docTipo = 96 // DNI
              docNro = dniCliente.dniCuitCuil
            }
            // Si no tiene documento o es cliente genérico (id=1), queda como CF (99/0)
          }
        }

        // 2.1) Condición frente al IVA del receptor (RG 5616)
        // Debes mapear docTipo o tu lógica de cliente a un ID válido:
        let condicionIvaReceptorId = condicionIva
        // ——————————————————————————————————————————
        // 3) Cálculo de neto e IVA
        let impNetoAjustado, impIVAajustado, ivaArray

        // ——————————————————————————————————————————
        // 4) Determinar tipo de comprobante
        let cbteTipo
        switch (idTipoFactura.id) {
          case 1:
            cbteTipo = 1
            break // Factura A
          case 2:
            cbteTipo = 6
            break // Factura B
          case 3:
            cbteTipo = 11
            break // Factura C
          default:
            throw new Error("Tipo de factura inválido")
        }

        // ——————————————————————————————————————————
        // 5) Preparar ImpNeto, ImpIVA e Iva según A, B o C
        if (cbteTipo === 11) {
          // Factura C: no lleva IVA, neto = total
          impNetoAjustado = Number.parseFloat(precioTotalModificado).toFixed(2)
          impIVAajustado = 0
          ivaArray = []
        } else {
          // Factura A y B: informan IVA detallado por alícuotas
          // ✅ Pasar descuento en valor para distribución proporcional por grupos de IVA
          const descuentoEnValor = Number(tempValorDescuento) || 0
          ivaArray = calcularIvaDetalladoPorAlícuotas(itemList, descuentoEnValor)

          // Calcular totales basados en el IVA detallado
          const totalBaseImponible = ivaArray.reduce((total, iva) => total + iva.BaseImp, 0)
          const totalIva = ivaArray.reduce((total, iva) => total + iva.Importe, 0)

          impNetoAjustado = Number.parseFloat(totalBaseImponible.toFixed(2))
          impIVAajustado = Number.parseFloat(totalIva.toFixed(2))
        }

        const precioTotal2dec = Number.parseFloat(precioTotalModificado.toFixed(2))

        console.log("ImpIVA ajustado:", impIVAajustado)
        console.log("ImpNeto ajustado:", impNetoAjustado)
        console.log("IVA Array detallado:", ivaArray)
        console.log("Items con impuestos:", itemList.map(item => ({
          descripcion: item.descripcion,
          impuestoData: item.impuestoData,
          ivaPercentage: item.ivaPercentage
        })))
        const dataParaAfip = {
          PtoVta: 1, // Punto de Venta 1 (por defecto, debe estar habilitado en AFIP)
          CbteTipo: cbteTipo, // Tipo de comprobante (6=Factura B, 1=Factura A, 11=Nota Crédito A, etc.)
          Concepto: 1, // Concepto de la operación (1=Productos y Servicios, 2=Mercaderías, 3=Servicios)
          //Concepto va a tener que ser una variable modificada dependiendo del cliente que utilice LUTENTE
          DocTipo: docTipo, // Tipo de documento del receptor (99=Consumidor Final, 80=CUIT, 86=CUIL, etc.)
          DocNro: docNro, // Número de documento del receptor (DNI o CUIT según DocTipo)
          CbteDesde: null, // Número de comprobante inicial (siempre igual a CbteHasta para un solo comprobante)
          CbteHasta: null, // Número de comprobante final
          CbteFch: fechaCbte, // Fecha del comprobante en formato YYYYMMDD
          ImpTotal: precioTotal2dec, // Importe total de la factura (suma de neto, IVA, tributos y exentas)
          ImpTotConc: 0, // Importe de operaciones no gravadas (conc., exentas)
          // Son aquellas ventas o servicios que están exentos de tributar IVA por la ley impositiva.
          //Ejemplo : Ejemplos: Servicios médicos. Venta de libros. Alquileres para vivienda.
          ImpNeto: impNetoAjustado, // Importe neto gravado (base imponible)
          ImpOpEx: 0, // Importe de operaciones exentas
          //Las operaciones exentas están explícitamente excluidas del impuesto por cuestiones legales.
          //Ejemplo: Ventas a entidades diplomáticas. Ventas a organismos internacionales. Servicios de transporte de pasajeros.Exportaciones de bienes y servicios (ya que el IVA se compensa en el exterior).
          ImpIVA: impIVAajustado, // Importe de IVA
          ImpTrib: 0, // Importe de tributos internos
          //No tiene que ver con el IVA, sino con otros impuestos locales, provinciales o nacionales.
          MonId: "PES", // Código de moneda (p.ej. 'PES' para pesos argentinos)
          MonCotiz: 1, // Cotización de la moneda (1 si es moneda local)
          //Iva: [ { Id: 5, BaseImp: 100.00, Importe: 21.00 } ], //ESTO ES CUANDO LLEGA IVA
          // Sólo incluyo Iva si NO es C
          // ← Nuevo campo obligatorio RG 5616
          CondicionIVAReceptorId: condicionIvaReceptorId,

          ...(cbteTipo !== 11 && { Iva: ivaArray }),
          ItemsList: itemList, //Items de la transaccion
          DniCuitCliente: dniCliente.dniCuitCuil,
          ApellidoNombre: `${dniCliente.descripcion} ${dniCliente.apellido}`,
          nombreCliente: `${dniCliente.descripcion}`,
          apellidoCliente: `${dniCliente.apellido}`,
          telefonoCliente: dniCliente.telefono || "",
          emailCliente: dniCliente.email || "",
          direccionCliente: dniCliente.direccion || "",
          localidadCliente: dniCliente.localidad || "",
          provinciaCliente: dniCliente.provincia || "",
          idTipoFacturaTransaccion: tipoFacturaId,
          idTransaccion: idTransaccion,
          abrirPdf: true,
        }

        const parametrosEmisorAfip = await getParametrosGlobales(['afipsdk_condicion_iva', 'afipsdk_domicilio', 'afipsdk_email', 'afipsdk_inicio_actividades', 'afipsdk_telefono', 'afipsdk_razon_social', 'afipsdk_cuit']);

        const payloadAfipCompleto = {
          ...dataParaAfip,
          // adiciona aquí cualquier campo extra:
          ...parametrosEmisorAfip,
          subtotal: precioBaseTotal,
          iva: precioTotalConImpuestos - precioBaseTotal,
          ivaArray: ivaArray, // ✅ IVA detallado por alícuotas para AFIP
        }
        const respAfip = await enviarFacturaAfip(payloadAfipCompleto)
        setAfipVentaData(respAfip)
        setAfipVentaPayload(payloadAfipCompleto)
      }
      // Actualizar impuestos de los items si han sido modificados
      console.log("Actualizando impuestos de items...")
      const impuestosActualizados = []

      for (const itemId in impuestosModificados) {
        const cambio = impuestosModificados[itemId]
        console.log(`Verificando cambio para item ${itemId}:`, cambio)

        if (cambio && cambio.porcentajeNuevo !== undefined) {
          try {
            console.log(`Actualizando impuesto del item ${itemId} de ${cambio.porcentajeOriginal}% a ${cambio.porcentajeNuevo}%`)

            // Buscar el impuesto original del item para obtener el ID correcto
            const itemImpuestoOriginal = itemImpuestos[itemId];
            console.log(`Impuesto original del item ${itemId}:`, itemImpuestoOriginal)

            const idImpuestoParaActualizar = itemImpuestoOriginal?.idImpuesto || cambio.idImpuesto;
            console.log(`ID de impuesto para actualizar:`, idImpuestoParaActualizar)

            if (idImpuestoParaActualizar) {
              await updateImpuestoItem(
                itemId,
                idImpuestoParaActualizar,
                cambio.porcentajeNuevo
              )
              impuestosActualizados.push(cambio.descripcion)
            } else {
              console.log(`No se pudo actualizar impuesto del item ${itemId}: no se encontró ID de impuesto`)
            }
          } catch (error) {
            console.error(`Error al actualizar impuesto del item ${itemId}:`, error)
          }
        } else {
          console.log(`Cambio no válido para item ${itemId}:`, cambio)
        }
      }

      console.log("Transacción generada:", transaccionData)

      setTransaccionData(transaccionData)
      setItemsTransaccion(itemList)
      setOpenModal(true)


      setSelectedItem(null)
      setSearchText("")
      setCantidadTransaccionItem("")
      setItemList([])
      setValorDescuento(0)
      setPorcentajeDescuento(0)
      setTempValorDescuento("0")
      setTempPorcentajeDescuento("0")
      setTempPorcentajeInteres("0")
      setPrecioBaseTotal(0)
      setMontoTotalDescuentos(0)
      setMontoTotalIntereses(0)
      setAplicarDescuento(false)
      setAplicarInteres(false)
      setPrecioTotal(0)
      setCantidadItemsSelect(0)
      setSelectedTipoMedioDePago(null)
      setSelectedMedioDePago(null)
      setHasOpenedCashModal(false)
      setCashPaidAmount(null)
      setHasOpenedChequeModal(false)
      setDescripcionCheque(null)

      // ✅ Limpiar descuentos de items (aunque itemList ya está vacío, es buena práctica)
      //limpiarDescuentosItems()

      // 🔄 Mensaje diferenciado según modo edición o creación
      let mensajeExito = id ? "Venta actualizada correctamente" : "Venta generada correctamente"
      if (preciosActualizados.length > 0) {
        mensajeExito += `. Precios actualizados para: ${preciosActualizados.join(", ")}`
      }
      if (impuestosActualizados.length > 0) {
        mensajeExito += `. Impuestos modificados para: ${impuestosActualizados.join(", ")}`
      }
      showSnackbar(mensajeExito, "success")
    } catch (error) {
      console.error("Error al generar la venta:", error)
      showSnackbar("Error al generar la venta", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateItemPorPrecio = async (index, newPrecio) => {
    try {
      // Validar y limpiar el precio
      const precioNumerico = Number.parseFloat(newPrecio)
      if (isNaN(precioNumerico) || precioNumerico <= 0) {
        showSnackbar("El precio debe ser un número válido mayor a 0", "error")
      }

      const updatedItems = await Promise.all(
        itemList.map(async (it, i) => {
          if (i === index) {
            return {
              ...it,
              precio: precioNumerico,
            }
          }
          return it
        }),
      )
      setItemList(updatedItems)

      // Recalcular totales después de actualizar el precio
      updateTotals(updatedItems)

      // No mostrar mensaje por cada actualización individual
    } catch (error) {
      console.error("Error actualizando el item:", error)
      showSnackbar("Error al actualizar el precio", "error")
    }
  }

  // ************************************************************************************************************************************************************
  // ************************************************************************************************************************************************************
  // ************************************************************************************************************************************************************
  // ************************************************************************************************************************************************************
  // ************************************************************************************************************************************************************
  // ************************************************************************************************************************************************************

  // Renderizado del componente
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#f5f5f5",
        position: "relative",
      }}
    >
      {/* Render condicional: Mobile/Tablet o Desktop */}
      {isMobileOrTablet ? (
        // Layout móvil/tablet
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleTransaccionSubmit(e, true)
          }}
        >
          <MobileVentaLayout
            // Cliente
            dniCliente={dniCliente}
            setDniCliente={setDniCliente}
            searchTextCliente={searchTextCliente}
            setSearchTextCliente={setSearchTextCliente}
            entidadesClientesTresLetras={entidadesClientesTresLetras}
            controlarDniIngresadoSiExiste={controlarDniIngresadoSiExiste}
            checkboxFacturaNegro={checkboxFacturaNegro}
            setCheckboxFacturaNegro={setCheckboxFacturaNegro}
            // Productos
            selectedItem={selectedItem}
            handleItemChange={handleItemChange}
            searchText={searchText}
            setSearchText={setSearchText}
            itemTresLetras={itemTresLetras}
            generateItemLabel={generateItemLabel}
            selectedUbicacion={selectedUbicacion}
            setSelectedUbicacion={setSelectedUbicacion}
            ubicaciones={ubicaciones}
            selectedNegocio={selectedNegocio}
            setSelectedNegocio={setSelectedNegocio}
            negocios={negocios}
            handleAddItem={handleAddItem}
            // Items
            itemList={itemList}
            handleDeleteItem={handleDeleteItem}
            cantidadInputs={cantidadInputs}
            setCantidadInputs={setCantidadInputs}
            handleUpdateItem={handleUpdateItem}
            precioEnVentaEditable={precioEnVentaEditable}
            handleUpdateItemPorPrecio={handleUpdateItemPorPrecio}
            // Totales
            precioTotal={precioTotal}
            // Footer
            onConfigClick={() => setMobileConfigPanelOpen(true)}
            isButtonDisabled={isButtonDisabled}
            theme={theme}
          />
        </form>
      ) : (
        // Layout desktop (original)
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 2,
            overflow: "auto",
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleTransaccionSubmit(e, true)
            }}
          >
            <Grid container spacing={2} sx={{ padding: "0px !important" }}>
            {/* Left Column - Productos y selección de Cliente */}
            <Grid item xs={12} md={9} sx={{ padding: "0px !important" }}>
              {/* Bloque para seleccionar Cliente y agregar entidad */}

              <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                    <ReceiptIcon sx={{ mr: 1, color: "rgb(244, 177, 14)" }} />
                    Generar Venta
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      mb: 2,
                      gap: 2,
                    }}
                  >
                    <Autocomplete
                      size="small"
                      fullWidth
                      disablePortal
                      options={entidadesClientesTresLetras.map((it) => ({
                        label: `${it.descripcion} ${it.apellido} ${it.dniCuitCuil}`,
                        descripcion: it.descripcion || null,
                        dniCuitCuil: it.dniCuitCuil,
                        id: it.id,
                        apellido: it.apellido,
                        telefono: it.telefono || null,
                        email: it.email || null,
                        direccion: it.direccion || null,
                        localidad: it.localidad || null,
                        provincia: it.provincia || null,
                        idTipoEntidad: it.idTipoEntidad,
                        // condicionIva: it.condicionIva || null, // Temporalmente comentado
                      }))}
                      value={dniCliente}
                      onChange={(event, newValue) => setDniCliente(newValue)}
                      onInputChange={(event, newInputValue) => setSearchTextCliente(newInputValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cliente - DNI / CUIT / CUIL"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <PersonIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <Tooltip title="Verificar Cliente">
                      <IconButton color="primary" onClick={controlarDniIngresadoSiExiste} sx={{ ml: 1 }}>
                        <PersonAddAltIcon />
                      </IconButton>
                    </Tooltip>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: { xs: "100%", sm: "10%" },
                        justifyContent: { xs: "flex-start", sm: "center" },
                        mt: { xs: 1, sm: 0 },
                      }}
                    >
                      <Typography sx={{ mb: 0, mr: 1 }}>F</Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!checkboxFacturaNegro}
                            onChange={(e) => {
                              setCheckboxFacturaNegro(!e.target.checked)
                              // Si se desmarca el checkbox (checkboxFacturaNegro se vuelve true), resetear descuento en valor
                              if (e.target.checked) {
                                setTempValorDescuento("0")
                              }
                            }} // *FACTURAR O NO FACTURAR*
                          />
                        }
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 1.5,
                      marginTop: "10px !important",
                    }}
                  >
                    <Autocomplete
                      size="small"
                      disablePortal
                      options={(itemTresLetras || []).map((it) => ({
                        label: generateItemLabel(it),
                        descripcion: it.descripcion,
                        codigo: it.codigo,
                        id: it.id,
                        codigoScanner: it.codigoScanner,
                        usaGestionLotes: it.usaGestionLotes || false,
                      }))}
                      value={selectedItem}
                      onChange={handleItemChange}
                      onInputChange={(event, newInputValue) => setSearchText(newInputValue)}
                      sx={{ flexGrow: 1, width: { xs: "100%", sm: "auto" } }}
                      disabled={!dniCliente} // <-- Deshabilita si no hay entidad
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Buscar producto"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <SearchIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          inputRef={autocompleteRef}
                          onKeyDown={handleKeyDown}
                          disabled={!dniCliente} // <-- Deshabilita si no hay entidad
                        />
                      )}
                    />
                    {/* <TextField
                      size="small"
                      label="Cantidad"
                      variant="outlined"
                      value={cantidadTransaccionItem}
                      onChange={(e) => setCantidadTransaccionItem(e.target.value)}
                      inputRef={cantidadRef}
                      onKeyDown={handleKeyDown}
                      sx={{ width: 100 }}
                      disabled={!dniCliente} // <-- Deshabilita si no hay entidad
                    /> */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Autocomplete
                        size="small"
                        disablePortal
                        options={ubicaciones.map((it) => ({
                          id: it.id,
                          label: it.descripcion,
                        }))}
                        value={selectedUbicacion}
                        onChange={(event, newValue) => {
                          setSelectedUbicacion(newValue)
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Ubicación"
                            size="small"
                            sx={{ width: { xs: "100%", sm: 180, md: 200 } }}
                          />
                        )}
                      />
                      <Autocomplete
                        size="small"
                        disablePortal
                        options={negocios.map((it) => ({
                          id: it.id,
                          label: it.descripcion,
                        }))}
                        value={selectedNegocio}
                        onChange={(event, newValue) => {
                          setSelectedNegocio(newValue)
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Negocio"
                            size="small"
                            sx={{ width: { xs: "100%", sm: 180, md: 200 } }}
                          />
                        )}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      onClick={handleAddItem}
                      ref={buttonRef}
                      onKeyDown={handleKeyDownAndSubmit}
                      startIcon={<AddShoppingCartIcon />}
                      sx={{ backgroundColor: "#5b5b5f", color: "white", width: { xs: "100%", sm: "auto" } }}
                    >
                      Agregar
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              <Card elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
                {/* Modificando TableContainer para que use toda la altura disponible de la pantalla */}
                <TableContainer
                  sx={{
                    maxHeight: {
                      xs: "calc(100vh - 300px)", // En móviles, usa toda la altura menos espacio para otros elementos
                      md: "calc(100vh - 250px)", // En desktop, usa toda la altura menos espacio para otros elementos
                    },
                    overflowX: "auto",
                    overflowY: "auto", // Asegura que aparezca scroll vertical cuando sea necesario
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Producto</StyledTableCell>
                        {/* 🆕 Columna Info Lote - solo si el parámetro global está activo Y hay items con lotes */}
                        {(() => {
                          const mostrarColumna = gestionDeLotesActiva && itemList.some(it => it.usaGestionLotes);
                          console.log("🔍 Renderizando TableHead - Mostrar columna lote:", mostrarColumna, {
                            gestionDeLotesActiva,
                            itemsConLotes: itemList.filter(it => it.usaGestionLotes).length,
                            totalItems: itemList.length
                          });
                          return mostrarColumna ? (
                            <StyledTableCell align="center" sx={{ display: { xs: "none", md: "table-cell" } }}>
                              Info Lote
                            </StyledTableCell>
                          ) : null;
                        })()}
                        <StyledTableCell align="right">Cantidad</StyledTableCell>
                        <StyledTableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                          Stock
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                          Precio Unit.
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                          Descuento
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                          Interés
                        </StyledTableCell>
                        <StyledTableCell align="right">Total</StyledTableCell>
                        {mostrarIva && !checkboxFacturaNegro && (
                          <StyledTableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                            IVA
                          </StyledTableCell>
                        )}
                        <StyledTableCell align="center">Acciones</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemList.length > 0 ? (
                        itemList.map((it, index) => {

                          const itemIvaPercentage = it.impuestoData?.porcentaje || it.ivaPercentage || 21
                          const precioSinIva = checkboxFacturaNegro
                            ? truncateToTwoDecimals(
                              (it.precio / (1 + itemIvaPercentage / 100)) *
                              (1 - (it.porcentajeDescuento || 0) / 100) *
                              (1 + (it.porcentajeInteres || 0) / 100),
                            )
                            : it.precio
                          const montoIva = checkboxFacturaNegro
                            ? truncateToTwoDecimals(precioSinIva * (itemIvaPercentage / 100))
                            : 0
                          const rowKey = `${it.idItem}-${it.idUbicacion}`;


                          return (
                            <StyledTableRow key={rowKey}>
                              <TableCell sx={{ py: 0.5 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {it.codigo} - {it.descripcion}
                                </Typography>
                              </TableCell>
                              
                              {/* 🆕 Columna Info Lote - solo si el parámetro global está activo Y hay items con lotes */}
                              {gestionDeLotesActiva && itemList.some(item => item.usaGestionLotes) && (
                                <TableCell 
                                  align="center" 
                                  sx={{ display: { xs: "none", md: "table-cell" }, py: 0.5, cursor: it.usaGestionLotes ? 'pointer' : 'default' }}
                                  onClick={() => {
                                    if (it.usaGestionLotes) {
                                      setItemLoteSeleccionado(it);
                                      setModalLotesAbierto(true);
                                    }
                                  }}
                                >
                                  {it.usaGestionLotes ? (
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center',
                                      gap: 0.5,
                                      '&:hover': { 
                                        backgroundColor: 'rgba(244, 147, 14, 0.1)',
                                        borderRadius: 1,
                                        padding: '4px 8px'
                                      }
                                    }}>
                                      {lotesDelCarrito[it.idItem] && lotesDelCarrito[it.idItem].length > 0 ? (
                                        <>
                                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgb(244, 147, 14)' }}>
                                            {lotesDelCarrito[it.idItem][0].numeroLote}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            ({new Date(lotesDelCarrito[it.idItem][0].fechaVencimiento).toLocaleDateString('es-AR')})
                                          </Typography>
                                          {lotesDelCarrito[it.idItem].length > 1 && (
                                            <Typography variant="caption" sx={{ 
                                              color: 'rgb(244, 147, 14)', 
                                              fontWeight: 600,
                                              backgroundColor: 'rgba(244, 147, 14, 0.2)',
                                              borderRadius: '50%',
                                              px: 0.8,
                                              py: 0.2
                                            }}>
                                              +{lotesDelCarrito[it.idItem].length - 1}
                                            </Typography>
                                          )}
                                        </>
                                      ) : (
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                          Click para seleccionar
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                              )}
                              
                              <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" }, py: 0.5 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={cantidadInputs[rowKey] ?? ""}
                                  onChange={(e) => {
                                    setCantidadInputs((prev) => ({ ...prev, [rowKey]: e.target.value }));
                                  }}
                                  inputProps={{
                                    step: "0.001",
                                    min: "0.001",
                                  }}
                                  onBlur={() => {
                                    const raw = cantidadInputs[rowKey] ?? "";
                                    const parsed = Number.parseFloat(raw);
                                    const finalValue = isNaN(parsed) || parsed <= 0 ? it.cantidad : parsed;
                                    handleUpdateItem(
                                      index,
                                      it.descripcion,
                                      finalValue,
                                      it.porcentajeDescuento,
                                      it.porcentajeInteres,
                                    );
                                    setCantidadInputs((prev) => ({ ...prev, [rowKey]: finalValue.toString() }));
                                  }}
                                  sx={{
                                    width: { xs: "100%", sm: 80 },
                                    "& .MuiInputBase-root": {
                                      borderRadius: 1,
                                      height: 32, // Reducir altura del input
                                    },
                                  }}
                                  InputProps={{
                                    endAdornment:
                                      it.cantidad > it.stock ? (
                                        <Tooltip title="Cantidad mayor a stock">
                                          <ErrorOutlineIcon
                                            sx={{
                                              color: theme.palette.error.main,
                                              cursor: "pointer",
                                            }}
                                          />
                                        </Tooltip>
                                      ) : null,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" }, py: 0.5 }}>
                                {it.stock}
                              </TableCell>
                              {precioEnVentaEditable ? (
                                <TableCell align="right" sx={{ py: 0.5 }}>
                                  <TextField
                                    type="text"
                                    size="small"
                                    value={precioInputs[rowKey] ?? formatInputWithSeparators(it.precio || 0, localeConfig.locale)}
                                    onChange={(e) => {
                                      const inputValue = e.target.value
                                      const { locale, decimalSeparator } = localeConfig

                                      if (inputValue === "") {
                                        setPrecioInputs((prev) => ({ ...prev, [rowKey]: "" }))
                                        return
                                      }

                                      const parsedValue = parseLocalizedNumber(inputValue, locale)

                                      if (parsedValue === null && inputValue.trim() !== "") {
                                        return // Keep previous valid input if current is unparseable
                                      }

                                      let formattedValue = parsedValue !== null
                                        ? formatInputWithSeparators(parsedValue, locale)
                                        : ""

                                      const hasDecimalSeparator = inputValue.includes(decimalSeparator)
                                      const endsWithDecimal = inputValue.endsWith(decimalSeparator)
                                      const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

                                      // Special handling for decimal separator at the end or during fractional input
                                      if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                                        formattedValue += decimalSeparator
                                      } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                                        const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                                        const integerPart = formattedValue.split(decimalSeparator)[0]
                                        formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
                                      }

                                      setPrecioInputs((prev) => ({ ...prev, [rowKey]: formattedValue }))

                                      // Keep cursor at the end
                                      requestAnimationFrame(() => {
                                        const input = e.target
                                        if (input) {
                                          input.setSelectionRange(formattedValue.length, formattedValue.length)
                                        }
                                      })
                                    }}
                                    onBlur={(e) => {
                                      const { locale } = localeConfig
                                      const parsed = parseLocalizedNumber(e.target.value, locale)
                                      if (parsed !== null && parsed >= 0) {
                                        const formatted = formatInputWithSeparators(parsed, locale)
                                        setPrecioInputs((prev) => ({ ...prev, [rowKey]: formatted }))
                                        handleUpdateItemPorPrecio(index, parsed)
                                      } else if (e.target.value === "") {
                                        setPrecioInputs((prev) => ({ ...prev, [rowKey]: "" }))
                                        handleUpdateItemPorPrecio(index, it.precio || 0)
                                      }
                                    }}
                                    sx={{
                                      width: 100,
                                      "& .MuiInputBase-root": {
                                        borderRadius: 1,
                                        height: 32, // Reducir altura del input
                                      },
                                    }}
                                    InputProps={{
                                      startAdornment: <span>$</span>,
                                      inputProps: {
                                        inputMode: "decimal",
                                        autoComplete: "off",
                                      },
                                    }}
                                  />
                                </TableCell>
                              ) : (
                                <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" }, py: 0.5 }}>
                                  {new Intl.NumberFormat("es-AR", {
                                    style: "currency",
                                    currency: "ARS",
                                  }).format(it.precio)}
                                </TableCell>
                              )}

                              <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" }, py: 0.5 }}>
                                <TextField
                                  type="number"
                                  value={it.porcentajeDescuento || 0}
                                  size="small"
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    const updatedItems = [...itemList];
                                    updatedItems[index] = {
                                      ...updatedItems[index],
                                      porcentajeDescuento: Number(newValue) || 0
                                    };
                                    setItemList(updatedItems);
                                  }}
                                  onBlur={(e) =>
                                    handleUpdateItem(
                                      index,
                                      it.descripcion,
                                      it.cantidad,
                                      e.target.value,
                                      it.porcentajeInteres,
                                    )
                                  }
                                  sx={{
                                    width: { xs: "100%", sm: 80 },
                                    "& .MuiInputBase-root": {
                                      borderRadius: 1,
                                      height: 32, // Reducir altura del input
                                    },
                                  }}
                                  InputProps={{ endAdornment: <span>%</span> }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" }, py: 0.5 }}>
                                <TextField
                                  type="number"
                                  value={it.porcentajeInteres || 0}
                                  size="small"
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    const updatedItems = [...itemList];
                                    updatedItems[index] = {
                                      ...updatedItems[index],
                                      porcentajeInteres: Number(newValue) || 0,
                                    };
                                    setItemList(updatedItems);
                                  }}
                                  onBlur={(e) =>
                                    handleUpdateItem(
                                      index,
                                      it.descripcion,
                                      it.cantidad,
                                      it.porcentajeDescuento,
                                      e.target.value,
                                    )
                                  }
                                  sx={{
                                    width: { xs: "100%", sm: 80 },
                                    "& .MuiInputBase-root": {
                                      borderRadius: 1,
                                      height: 32,
                                    },
                                  }}
                                  InputProps={{ endAdornment: <span>%</span> }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" }, py: 0.5 }}>
                                <Typography fontWeight="medium">
                                  {new Intl.NumberFormat("es-AR", {
                                    style: "currency",
                                    currency: "ARS",
                                  }).format(getItemLineTotal(it))}
                                </Typography>
                              </TableCell>
                              {!checkboxFacturaNegro && mostrarIva && (
                              <TableCell align="right">
                                <Box sx={{ position: 'relative' }}>
                                  <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select
                                      value={itemIvaPercentage}
                                      onChange={(e) => {
                                        const newIvaPercentage = Number.parseFloat(e.target.value) || 0
                                        // Usar la función específica para actualizar impuesto
                                        handleUpdateItemImpuesto(index, newIvaPercentage)
                                      }}
                                      displayEmpty
                                      sx={{
                                        "& .MuiInputBase-root": {
                                          borderRadius: 1,
                                        },
                                      }}
                                    >
                                      {getImpuestosValidosParaItem(it.idItem, it.impuestoData).map((impuesto) => (
                                        <MenuItem key={impuesto.id} value={impuesto.porcentaje}>
                                          {impuesto.porcentaje}%
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  {impuestosModificados[it.idItem] && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: -8,
                                        right: -8,
                                        backgroundColor: '#ff9800',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: 16,
                                        height: 16,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      !
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                            )}
                              <TableCell align="center" sx={{ py: 0.5 }}>
                                <Tooltip title="Eliminar ítem">
                                  <IconButton color="error" size="small" onClick={() => handleDeleteItem(index)} sx={{ padding: '4px' }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </StyledTableRow>
                          )
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={mostrarIva && !checkboxFacturaNegro ? 9 : 8} align="center" sx={{ py: 3 }}>
                            <Typography variant="body1" color="text.secondary">
                              No hay productos agregados
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Busque y agregue productos para comenzar la venta
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={mostrarIva && !checkboxFacturaNegro ? 9 : 8} align="right">
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleDeleteAllItems}
                            disabled={itemList.length === 0}
                            sx={{ mt: 1, mb: 1 }}
                          >
                            Eliminar todos
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            {/* Right Column - Resumen y Usuario */}
            <Grid item xs={12} md={3}>
              {/* Tarjeta de Usuario */}
              <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                    <PersonIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
                    Usuario
                  </Typography>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disablePortal
                    options={usuarios.map((it) => ({
                      label: `${it.usuario} ${it.nombre || ""} ${it.apellido || ""}`,
                      nombre: it.nombre,
                      apellido: it.apellido,
                      id: it.id,
                      usuario: it.usuario,
                    }))}
                    value={usuarioSeleccionado}
                    onChange={(event, newValue) => setUsuarioSeleccionado(newValue)}
                    renderInput={(params) => <TextField {...params} label="Usuario" />}
                  />
                </CardContent>
              </Card>

              {/* Resumen de Venta */}
              <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                    <ReceiptIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
                    Resumen de Venta
                  </Typography>

                  <Box
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.light, 0.1),
                      p: 2,
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body1">Subtotal:</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {new Intl.NumberFormat("es-AR", {
                          style: "currency",
                          currency: "ARS",
                        }).format(precioBaseTotal)}
                      </Typography>
                    </Box>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={aplicarDescuento}
                          onChange={(e) => {
                            const isChecked = e.target.checked
                            setAplicarDescuento(isChecked)
                            
                            // Limpiar descuentos al desmarcar
                            if (!isChecked) {
                              limpiarDescuentosItems()
                            }
                          }}
                          color="primary"
                        />
                      }
                      label="Aplicar Descuento"
                    />

                    <Collapse in={aplicarDescuento} timeout={300}>
                      <Box sx={{ mt: 1, mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          label="Descuento en Porcentaje (%)"
                          type="number"
                          value={tempPorcentajeDescuento}
                          onChange={(e) => setTempPorcentajeDescuento(e.target.value)}
                          onBlur={applyDiscounts}
                          InputProps={{
                            inputProps: { step: 0.01 },
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          fullWidth
                          size="small"
                        />
                        <Tooltip title="Limpiar todos los descuentos">
                          <IconButton 
                            onClick={limpiarDescuentosItems}
                            color="primary"
                            size="small"
                            sx={{ 
                              border: '1px solid',
                              borderColor: 'primary.main',
                              borderRadius: 1
                            }}
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Collapse>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={aplicarInteres}
                          onChange={(e) => {
                            const isChecked = e.target.checked
                            setAplicarInteres(isChecked)
                            if (!isChecked) {
                              limpiarInteresesItems()
                            }
                          }}
                          color="primary"
                        />
                      }
                      label="Aplicar Interés"
                    />

                    <Collapse in={aplicarInteres} timeout={300}>
                      <Box sx={{ mt: 1, mb: 1, display: "flex", gap: 1, alignItems: "center" }}>
                        <TextField
                          label="Interés en Porcentaje (%)"
                          type="number"
                          value={tempPorcentajeInteres}
                          onChange={(e) => setTempPorcentajeInteres(e.target.value)}
                          onBlur={applyInterest}
                          InputProps={{
                            inputProps: { step: 0.01 },
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          fullWidth
                          size="small"
                        />
                        <Tooltip title="Limpiar todos los intereses">
                          <IconButton
                            onClick={limpiarInteresesItems}
                            color="primary"
                            size="small"
                            sx={{
                              border: "1px solid",
                              borderColor: "primary.main",
                              borderRadius: 1,
                            }}
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Collapse>

                    {/* Mostrar Total Descuentos si hay descuentos */}
                    {montoTotalDescuentos > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          color: 'error.main'
                        }}
                      >
                        <Typography variant="body2">Total Descuentos:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          -{new Intl.NumberFormat("es-AR", {
                            style: "currency",
                            currency: "ARS",
                          }).format(montoTotalDescuentos)}
                        </Typography>
                      </Box>
                    )}

                    {montoTotalIntereses > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          color: "success.main",
                        }}
                      >
                        <Typography variant="body2">Total Intereses:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          +{new Intl.NumberFormat("es-AR", {
                            style: "currency",
                            currency: "ARS",
                          }).format(montoTotalIntereses)}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    {!aceptarImpuestos && checkboxFacturaNegro == false && impuestos && Array.isArray(impuestos) && impuestos.length > 0 ? (
                      impuestos
                        .filter((impuesto) => !impuesto.eliminado && !impuesto.incluidoEnPrecio)
                        .map((impuesto, index) => {
                          const montoImpuesto = (precioBaseTotal * impuesto.porcentaje) / 100
                          return (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Typography variant="body2">
                                {impuesto.descripcion} ({impuesto.porcentaje}%):
                              </Typography>
                              <Typography variant="body2">
                                {new Intl.NumberFormat("es-AR", {
                                  style: "currency",
                                  currency: "ARS",
                                }).format(montoImpuesto)}
                              </Typography>
                            </Box>
                          )
                        })
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
                        {/* No se encuentran impuestos aplicables */}
                      </Typography>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1,
                      }}
                    >
                      <Typography variant="h6">TOTAL:</Typography>
                      <Typography variant="h6" color="rgb(244, 147, 14)" fontWeight="bold">
                        {new Intl.NumberFormat("es-AR", {
                          style: "currency",
                          currency: "ARS",
                        }).format(precioTotal)}
                      </Typography>
                    </Box>
                  </Box>

                  {checkboxFacturaNegro == false && (
                    <Autocomplete
                      size="small"
                      fullWidth
                      disablePortal
                      options={tiposFacturas || []}
                      getOptionLabel={(option) => {
                        if (!option || typeof option !== 'object') return ""
                        return option.descripcion || ""
                      }}
                      value={idTipoFactura}
                      onChange={(event, newValue) => {
                        setIdTipoFactura(newValue)
                      }}
                      renderInput={(params) => <TextField {...params} label="Tipo de factura" />}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Medios de Pago */}
              <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                    <PaymentsIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
                    Medios de Pago
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      disablePortal
                      options={tipoMedioDePago || []}
                      getOptionLabel={(option) => {
                        if (!option || typeof option !== 'object') return ""
                        return option.descripcion || ""
                      }}
                      value={selectedTipoMedioDePago}
                      onChange={(event, newValue) => {
                        setSelectedTipoMedioDePago(newValue)
                      }}
                      renderInput={(params) => <TextField {...params} label="Tipo de medio de pago" />}
                    />

                    {mostrarButtonModalMedioDePago && (
                      <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={handleAbrirModalMedioDePago}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Ver medios de pago seleccionados
                      </Button>
                    )}

                    <Collapse in={openCollapse}>
                      <Box sx={{ mt: 2 }}>
                        <Autocomplete
                          size="small"
                          fullWidth
                          disablePortal
                          options={medioDePagoIdTipoEspecifico || []}
                          getOptionLabel={(option) => {
                            if (!option || typeof option !== 'object') return ""
                            return option.descripcion || ""
                          }}
                          value={selectedMedioDePago || null}
                          onChange={(event, newValue) => {
                            setSelectedMedioDePago(newValue)
                          }}
                          renderInput={(params) => <TextField {...params} label="Medio de pago específico" />}
                        />
                      </Box>
                    </Collapse>
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    size="large"
                    disabled={isButtonDisabled}
                    startIcon={<PointOfSaleIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: "bold",
                      boxShadow: 3,
                      backgroundColor: "green",
                      color: "white",
                    }}
                  >
                    GENERAR VENTA
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Box>
      )}

      {/* Drawer para configuración móvil/tablet */}
      {isMobileOrTablet && (
        <Drawer
          anchor="right"
          open={mobileConfigPanelOpen}
          onClose={() => setMobileConfigPanelOpen(false)}
          ModalProps={{
            keepMounted: true, // Mejor rendimiento en móvil
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: { xs: "100vw", sm: 400 },
              boxSizing: "border-box",
            },
          }}
        >
          <VentaConfigPanel
            // Usuario
            usuarioSeleccionado={usuarioSeleccionado}
            setUsuarioSeleccionado={setUsuarioSeleccionado}
            usuarios={usuarios}
            // Resumen
            precioBaseTotal={precioBaseTotal}
            precioTotal={precioTotal}
            aplicarDescuento={aplicarDescuento}
            setAplicarDescuento={setAplicarDescuento}
            tempPorcentajeDescuento={tempPorcentajeDescuento}
            setTempPorcentajeDescuento={setTempPorcentajeDescuento}
            applyDiscounts={applyDiscounts}
            limpiarDescuentosItems={limpiarDescuentosItems}
            montoTotalDescuentos={montoTotalDescuentos}
            aplicarInteres={aplicarInteres}
            setAplicarInteres={setAplicarInteres}
            tempPorcentajeInteres={tempPorcentajeInteres}
            setTempPorcentajeInteres={setTempPorcentajeInteres}
            applyInterest={applyInterest}
            limpiarInteresesItems={limpiarInteresesItems}
            montoTotalIntereses={montoTotalIntereses}
            aceptarImpuestos={aceptarImpuestos}
            checkboxFacturaNegro={checkboxFacturaNegro}
            impuestos={impuestos}
            // Tipo de factura
            idTipoFactura={idTipoFactura}
            setIdTipoFactura={setIdTipoFactura}
            tiposFacturas={tiposFacturas}
            // Medios de pago
            selectedTipoMedioDePago={selectedTipoMedioDePago}
            setSelectedTipoMedioDePago={setSelectedTipoMedioDePago}
            tipoMedioDePago={tipoMedioDePago}
            selectedMedioDePago={selectedMedioDePago}
            setSelectedMedioDePago={setSelectedMedioDePago}
            medioDePagoIdTipoEspecifico={medioDePagoIdTipoEspecifico}
            openCollapse={openCollapse}
            mostrarButtonModalMedioDePago={mostrarButtonModalMedioDePago}
            handleAbrirModalMedioDePago={handleAbrirModalMedioDePago}
            // Acciones
            onClose={() => setMobileConfigPanelOpen(false)}
            onGenerarVenta={(e) => {
              e.preventDefault()
              handleTransaccionSubmit(e, true)
            }}
            isButtonDisabled={isButtonDisabled}
            theme={theme}
          />
        </Drawer>
      )}

      {/* Modales */}
      <ModalEntidadCliente
        open={abrirModalEntidadCliente}
        handleClose={() => setAbrirModalEntidadCliente(false)}
        esClienteExistente={!!entidadExistente}
        entidad={entidadExistente}
        abrirCrearCliente={handleAbrirModalCrearCliente}
        crearVentaIgualmente={crearVentaIgualmente}
        abrirModificarCliente={abrirModificarCliente}
        deDondeViene="VENTA"
        tipoEntidad="cliente"
      />

      <CrearEntidadClienteModalComponent
        open={abrirModalCrearCliente}
        handleClose={handleCerrarModalCrearCliente}
        entidad={entidadAEditar}
        isEditing={isEditing}
        handleAlerta={handleAlerta}
        onClienteCreado={handleClienteCreado}
        tipoEntidad="cliente"
      />

      <Modal open={openModalMedioPago} onClose={handleCloseModalMedioPago}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
              <PaymentsIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
              Selección de Medios de Pago
            </Typography>
            <IconButton onClick={handleCloseModalMedioPago} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                fullWidth
                options={tipoMedioDePago || []}
                getOptionLabel={(option) => {
                  if (!option || typeof option !== 'object') return ""
                  return option.descripcion || ""
                }}
                value={modalTipoMedioDePagoSeleccionado}
                onChange={(event, newValue) => setModalTipoMedioDePagoSeleccionado(newValue)}
                renderInput={(params) => <TextField {...params} label="Tipo de medio de pago" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                fullWidth
                options={medioDePagoIdTipoEspecifico.map((it) => ({
                  label: it.descripcion,
                  id: it.id,
                }))}
                value={modalMedioDePagoSeleccionado}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setModalMedioDePagoSeleccionado(newValue.label)
                    setModalMedioDePagoSeleccionadoID(newValue.id)
                  } else {
                    setModalMedioDePagoSeleccionado("")
                    setModalMedioDePagoSeleccionadoID(null)
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Medio de Pago" />}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              gap: 1,
              backgroundColor: "transparent",
            }}
          >
            <TextField
              label="Monto a pagar"
              size="small"
              fullWidth
              value={modalMedioDePagoCantidad || ""}
              onChange={(e) => setModalMedioDePagoCantidad(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <Button variant="contained" onClick={handleAddNuevoMedioDePago} startIcon={<AddCircleOutlineIcon />}>
              Agregar
            </Button>
          </Box>

          <Card variant="outlined" sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.light, 0.05) }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                Resumen de Pagos
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body1">Total a pagar:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {new Intl.NumberFormat("es-AR", {
                    style: "currency",
                    currency: "ARS",
                  }).format(precioTotal)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body1">Total medios de pago:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {new Intl.NumberFormat("es-AR", {
                    style: "currency",
                    currency: "ARS",
                  }).format(cantidaMedioDePagoList)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body1">Diferencia:</Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  color={precioTotal === cantidaMedioDePagoList ? "success.main" : "error.main"}
                >
                  {new Intl.NumberFormat("es-AR", {
                    style: "currency",
                    currency: "ARS",
                  }).format(precioTotal - cantidaMedioDePagoList)}
                </Typography>
              </Box>

              {Math.abs(cantidaMedioDePagoList - precioTotal) > 0.01 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  El total de medios de pago debe ser igual al total a pagar
                </Alert>
              )}
            </CardContent>
          </Card>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Medios de Pago Seleccionados
          </Typography>

          {medioDePagoList.length > 0 ? (
            <Box sx={{ maxHeight: 200, overflow: "auto", mb: 2 }}>
              {medioDePagoList.map((it, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.default, 0.7),
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <Typography variant="body2">{it.descripcion}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {new Intl.NumberFormat("es-AR", {
                        style: "currency",
                        currency: "ARS",
                      }).format(it.monto)}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => handleDeleteMedioDePago(index, it.monto)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center", py: 2 }}>
              No hay medios de pago seleccionados
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={handleCloseModalMedioPago}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleCloseModalMedioPago}
              disabled={Math.abs(cantidaMedioDePagoList - precioTotal) > 0.01}
            >
              Confirmar
            </Button>
          </Box>
        </Box>
      </Modal>

      {openModal && (
        <ModalFacturaComponent
          open={openModal}
          handleClose={handleCloseModal}
          transaccionData={transaccionData}
          impuestos={impuestos}
          itemsTransaccion={itemsTransaccion}
          entidadParaFactura={entidadFactura}
          usuarioParaFactura={usuarioSeleccionado}
          tipoDeFactura={checkboxFacturaNegro}
          deDondeViene={"crearVenta"}
          afipData={afipVentaData}
          afipPayload={afipVentaPayload}
        />
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Procesando...</Typography>
          </Box>
        </Box>
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Cash Payment Modal */}
      <CashPaymentModal
        open={openModalCashPayment}
        onClose={() => {
          setOpenModalCashPayment(false)
          // Resetear flags para que vuelva a pedir efectivo la próxima vez
          setHasOpenedCashModal(false)
          setCashPaidAmount(null)
        }}
        totalAmount={precioTotal}
        onConfirm={(amountPaid) => {
          setCashPaidAmount(amountPaid)
          setOpenModalCashPayment(false)
          handleTransaccionSubmit(null, true) // vuelve a lanzar la venta
        }}
      />
      <DescripcionChequeModal
        open={openChequeDescripcion}
        onClose={() => setOpenChequeDescripcion(false)}
        descripcionDelCheque={descripcionCheque}
        onConfirm={(descripcion) => {
          console.log("dato enviado", descripcion)
          setDescripcionCheque(descripcion)
          setOpenChequeDescripcion(false)
          handleTransaccionSubmit(null, true, descripcion) // vuelve a lanzar la venta
        }}
      />
      
      {/* 🆕 Modal de Selección de Lotes */}
      <SeleccionLotesModal
        open={modalLotesAbierto}
        onClose={() => setModalLotesAbierto(false)}
        item={itemLoteSeleccionado ? itemList.find(item => item.idItem === itemLoteSeleccionado.idItem) : null}
        idUbicacion={selectedUbicacion?.id}
        lotesIniciales={itemLoteSeleccionado ? lotesDelCarrito[itemLoteSeleccionado.idItem] : []}
        onConfirmar={(lotesSeleccionados) => {
          setLotesDelCarrito(prev => ({
            ...prev,
            [itemLoteSeleccionado.idItem]: lotesSeleccionados
          }));
          console.log("✅ Lotes actualizados:", lotesSeleccionados);
        }}
      />
    </Box>
  )
}

export default GestionGenerarVentaComponent
